/** XeAgents.ai**/
import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { DataPagination, usePagination } from "@/components/ui/data-pagination";
import {
  Plus,
  Search,
  Trash2,
  Edit,
  Bot,
  Upload,
  Sparkles,
  GitBranch,
  CheckCircle2,
  XCircle,
  Mic,
  Brain,
  Settings2,
  Wrench,
  Check,
  FileText,
  History,
  Wand2,
  Phone,
  PhoneCall,
  Copy,
  Download,
  X,
  Mail,
  Eye,
  Loader2,
  Languages,
} from "lucide-react";
import { AuthStorage } from "@/lib/auth-storage";
import { formatDate } from "@/lib/date-format";
import PromptTemplatesLibrary from "@/components/PromptTemplatesLibrary";
import AgentVersionHistory from "@/components/AgentVersionHistory";
import { useToast } from "@/hooks/use-toast";
import { buildAgentCreatePayload } from "@/lib/agent-payload";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import VoiceSearchPicker from "@/components/VoiceSearchPicker";
import { ElevenLabsVoicePicker, ELEVENLABS_LANGUAGES } from "@/components/ElevenLabsVoicePicker";
import VoicePreviewButton from "@/components/VoicePreviewButton";
import EditAgentDialog from "@/components/EditAgentDialog";
import ViewAgentDialog from "@/components/ViewAgentDialog";
import AgentTestCallDialog from "@/components/AgentTestCallDialog";
import NovaSonicAgentDialog from "@/components/NovaSonicAgentDialog";
import { filterToNovaLanguages, isNovaSonicLanguage } from "@/lib/nova-sonic-languages";
import { visibleAgents } from "@/lib/voice-engine";
import { folderDisplayName, foldersWithDocuments } from "@shared/knowledge-base-path";
import { withBase } from "@/lib/base-path";

interface Agent {
  id: string;
  type: "incoming" | "flow";
  name: string;
  voiceTone: string;
  personality: string;
  systemPrompt: string;
  elevenLabsAgentId: string | null;
  elevenLabsVoiceId: string | null;
  agentLink: string | null;
  language: string | null;
  llmModel: string | null;
  firstMessage: string | null;
  temperature: number | null;
  knowledgeBaseIds: string[] | null;
  knowledgeBaseFolderPath: string | null;
  config: any;
  flowId: string | null;
  maxDurationSeconds: number | null;
  voiceStability: number | null;
  voiceSimilarityBoost: number | null;
  voiceSpeed: number | null;
  transferEnabled: boolean | null;
  transferPhoneNumber: string | null;
  detectLanguageEnabled: boolean | null;
  endConversationEnabled: boolean | null;
  telephonyProvider: string | null;
  salesAgentEmails: string[] | null;
  createdAt: string;
  /** 'nova-sonic' agents are edited in NovaSonicAgentDialog, not the ElevenLabs form. */
  voiceProvider?: string | null;
  novaSonicVoice?: string | null;
  phoneNumberId?: string | null;
  flowName?: string | null;
  flowDescription?: string | null;
  connectedPhoneNumber?: {
    id: string;
    phoneNumber: string;
    friendlyName: string | null;
    type: 'twilio';
  } | null;
  /** Linked flow, attached by the agents list query for flow agents. */
  flow?: {
    id: string;
    name: string;
    description: string | null;
  } | null;
}

const AGENT_LANGUAGE_FILTERS = [
  { value: "en", label: "🇺🇸 English" },
  { value: "es", label: "🇪🇸 Spanish" },
  { value: "fr", label: "🇫🇷 French" },
  { value: "de", label: "🇩🇪 German" },
  { value: "hi", label: "🇮🇳 Hindi" },
  { value: "it", label: "🇮🇹 Italian" },
  { value: "ja", label: "🇯🇵 Japanese" },
  { value: "pt", label: "🇧🇷 Portuguese" },
  { value: "ar", label: "🇸🇦 Arabic" },
  { value: "sv", label: "🇸🇪 Swedish" },
  { value: "pl", label: "🇵🇱 Polish" },
  { value: "zh", label: "🇨🇳 Chinese" },
  { value: "ko", label: "🇰🇷 Korean" },
  { value: "ru", label: "🇷🇺 Russian" },
];

interface Voice {
  voice_id: string;
  name: string;
  category?: string;
  labels?: Record<string, string>;
  preview_url?: string;
}

// LLM cost estimates per minute (in USD)
const MODEL_COSTS: Record<
  string,
  { llm: number; name: string; speed: string }
> = {
  // ElevenLabs Models
  "glm-45-air-fp8": { llm: 0.0106, name: "GLM-4.5-Air", speed: "Balanced" },
  "qwen3-30b-a3b": { llm: 0.0033, name: "Qwen3-30B-A3B", speed: "Ultra Fast" },
  "gpt-oss-120b": { llm: 0.02, name: "GPT-OSS-120B", speed: "High Quality" },

  // Google Models
  "gemini-2.5-flash": {
    llm: 0.005,
    name: "Gemini 2.5 Flash",
    speed: "Very Fast",
  },
  "gemini-2.5-flash-lite": {
    llm: 0.003,
    name: "Gemini 2.5 Flash Lite",
    speed: "Ultra Fast",
  },
  "gemini-2.0-flash": {
    llm: 0.004,
    name: "Gemini 2.0 Flash",
    speed: "Very Fast",
  },
  "gemini-2.0-flash-lite": {
    llm: 0.002,
    name: "Gemini 2.0 Flash Lite",
    speed: "Ultra Fast",
  },

  // OpenAI Models
  "gpt-4o": { llm: 0.02, name: "GPT-4o", speed: "Balanced" },
  "gpt-4o-mini": { llm: 0.006, name: "GPT-4o Mini", speed: "Fast" },
  "gpt-4-turbo": { llm: 0.04, name: "GPT-4 Turbo", speed: "High Quality" },
  "gpt-3.5-turbo": { llm: 0.003, name: "GPT-3.5 Turbo", speed: "Very Fast" },

  // Anthropic Models
  "claude-3-5-sonnet": {
    llm: 0.06,
    name: "Claude 3.5 Sonnet",
    speed: "High Quality",
  },
  "claude-3-haiku": { llm: 0.01, name: "Claude 3 Haiku", speed: "Fast" },

  // Kie.ai Claude Models (via Kie API aggregator)
  "claude-sonnet-4-6": {
    llm: 0.05,
    name: "Claude Sonnet 4.6 (Kie)",
    speed: "Excellent",
  },
  "claude-sonnet-4-20250514": {
    llm: 0.05,
    name: "Claude Sonnet 4 (Kie)",
    speed: "High Quality",
  },
  "claude-3-5-sonnet-20241022": {
    llm: 0.04,
    name: "Claude 3.5 Sonnet (Kie)",
    speed: "High Quality",
  },
};

const VOICE_COST = 0.1; // $0.10 per minute for voice service


function EstimatedCost({ model }: { model: string }) {
  const { t } = useTranslation();
  const modelInfo = MODEL_COSTS[model];

  // Fetch LLM margin from admin settings
  const { data: marginData } = useQuery<{ llm_margin_percentage: number }>({
    queryKey: ["/api/settings/llm-margin"],
  });

  if (!modelInfo) return null;

  const marginPercentage = marginData?.llm_margin_percentage || 30;
  const baseLlmCost = modelInfo.llm;
  const llmCostWithMargin = baseLlmCost * (1 + marginPercentage / 100);
  const totalCost = VOICE_COST + llmCostWithMargin;

  return (
    <div className="mt-2 p-3 bg-secondary/50 rounded-md text-xs">
      <div className="font-medium mb-1">
        {t("agents.cost.estimatedBreakdown", { margin: marginPercentage })}
      </div>
      <div className="space-y-1">
        <div className="flex justify-between">
          <span className="text-muted-foreground">
            {t("agents.cost.voiceService")}
          </span>
          <span>${VOICE_COST.toFixed(3)}/min</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">
            {t("agents.cost.llm", { model: modelInfo.name })}
          </span>
          <span>${llmCostWithMargin.toFixed(3)}/min</span>
        </div>
        <div className="h-px bg-border my-2" />
        <div className="flex justify-between font-medium">
          <span>{t("agents.cost.totalCost")}</span>
          <span className="text-primary">${totalCost.toFixed(3)}/min</span>
        </div>
        <div className="mt-2 text-muted-foreground">
          {t("agents.cost.speed")} {modelInfo.speed} •{" "}
          {t("agents.cost.minCall")} ${(totalCost * 60).toFixed(2)}
        </div>
      </div>
    </div>
  );
}

/** The setter's own zone — a far better starting point than UTC. */
const AGENT_BROWSER_TIMEZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
})();

/** Every IANA zone the runtime knows, with the browser's own pinned to the top. */
const AGENT_TIMEZONE_OPTIONS: string[] = (() => {
  let zones: string[] = [];
  try {
    zones = (Intl as any).supportedValuesOf?.("timeZone") ?? [];
  } catch {
    zones = [];
  }
  if (!zones.length) {
    zones = [
      "UTC", "Asia/Riyadh", "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore",
      "Europe/London", "Europe/Berlin", "America/New_York", "America/Chicago",
      "America/Los_Angeles", "Australia/Sydney",
    ];
  }
  return [AGENT_BROWSER_TIMEZONE, ...zones.filter((z) => z !== AGENT_BROWSER_TIMEZONE)];
})();

export default function Agents() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | "incoming" | "flow">(
    "all"
  );
  const [languageFilter, setLanguageFilter] = useState<string>("all");
  
  // Bulk operations state
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  
  // Bulk operation handlers
  const handleSelectAgent = (agentId: string) => {
    setSelectedAgents(prev => 
      prev.includes(agentId) 
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    );
  };
  
  const handleSelectAll = () => {
    if (selectedAgents.length === filteredAgents.length) {
      setSelectedAgents([]);
    } else {
      setSelectedAgents(filteredAgents.map(a => a.id));
    }
  };
  
  const handleClearSelection = () => {
    setSelectedAgents([]);
  };
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingAgentForWizard, setEditingAgentForWizard] = useState<Agent | null>(null);
  const [knowledgeUploadOpen, setKnowledgeUploadOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [viewingAgent, setViewingAgent] = useState<Agent | null>(null);
  const [deletingAgent, setDeletingAgent] = useState<Agent | null>(null);
  const [testingAgent, setTestingAgent] = useState<Agent | null>(null);
  const [novaDialogOpen, setNovaDialogOpen] = useState(false);
  const [novaEditingAgent, setNovaEditingAgent] = useState<Agent | null>(null);
  // Voice engine the super admin chose in Global Settings (Nova Sonic unless set to ElevenLabs)
  const { data: voiceEngine } = useQuery<{ provider: string }>({ queryKey: ["/api/public/voice-provider"], staleTime: 60_000 });
  const novaIsDefault = voiceEngine?.provider === "nova-sonic";
  const openNovaDialog = () => {
    setNovaEditingAgent(null);
    setNovaDialogOpen(true);
  };
  const [formData, setFormData] = useState({
    type: "incoming" as "incoming" | "flow",
    name: "",
    phoneNumberId: "",
    voiceProvider: "elevenlabs" as "elevenlabs",
    voiceTone: "professional",
    personality: "helpful",
    systemPrompt: "",
    elevenLabsVoiceId: "",
    language: "en",
    timezone: AGENT_BROWSER_TIMEZONE,
    llmModel: "gpt-4o-mini",
    firstMessage: "Hello! How can I help you today?",
    temperature: 0.5,
    knowledgeBaseIds: [] as string[],
    knowledgeBaseFolderPath: "", // Folder-based knowledge base
    transferNumber: "",
    transferKeywords: [] as string[],
    // System Tools configuration
    transferEnabled: false,
    transferPhoneNumber: "",
    detectLanguageEnabled: false,
    endConversationEnabled: false,
    // Outbound Agent specific fields
    flowId: "",
    maxDurationSeconds: 600,
    voiceStability: 0.4,
    voiceSimilarityBoost: 0.85,
    voiceSpeed: 1.0,
    telephonyProvider: "twilio",
    // Sales Agent / Meeting Attendee Emails
    salesAgentEmails: [""] as string[],
    // Meeting Booking
    meetingBookingEnabled: false,
    // Meeting Host Details (required for flow agents)
    meetingHostName: "",
    meetingHostEmail: "",
    // Self-schedule link (e.g. Calendly) sent to prospect after booking
    meetingBookingUrl: "",
  });
  const [knowledgeData, setKnowledgeData] = useState({
    title: "",
    type: "document",
    content: "",
    // Required by /api/rag-knowledge/text. The legacy endpoint this dialog used to post
    // to accepted no folder at all, which is how documents ended up outside every
    // folder and therefore invisible to folder-based agent selection.
    folderPath: "",
  });

  // Animation state for success/failure feedback
  const [animationState, setAnimationState] = useState<
    "idle" | "success" | "error"
  >("idle");
  
  // AI Prompt Generation state
  const [aiGenerateOpen, setAiGenerateOpen] = useState(false);
  const [aiDescription, setAiDescription] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [isGeneratingFirstMessage, setIsGeneratingFirstMessage] = useState(false);
  const [isGeneratingStyle, setIsGeneratingStyle] = useState(false);

  const { data: allAgents = [], isLoading: agentsLoading } = useQuery<Agent[]>({
    queryKey: ["/api/agents"],
  });
  // With Nova Sonic selected by the super admin, agents that run on ElevenLabs are hidden
  const agents = useMemo(() => visibleAgents(allAgents, novaIsDefault), [allAgents, novaIsDefault]);

  // Fetch existing host email from Appointments page (calendar team members)
  interface ManualHostData {
    configured: boolean;
    hostEmail?: string;
    hostName?: string;
  }
  const { data: existingHostData } = useQuery<ManualHostData>({
    queryKey: ["/api/flow-automation/calendar-team-members/my-host"],
  });

  // Fetch user data to determine billing country (same pattern as Voices.tsx)
  const { data: userData, isLoading: isLoadingUserData } = useQuery({
    queryKey: ['/api/auth/me'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/auth/me');
      return res.json();
    },
  });

  // Determine voice provider based on billing country (same pattern as Voices.tsx)
  const normalizedBillingCountry =
    userData?.billingCountry?.toUpperCase() === 'IN' ||
    userData?.billingCountry?.toLowerCase() === 'india'
      ? 'IN'
      : 'US';

  // All orgs use ElevenLabs for voice AI
  const agentVoiceProvider = 'elevenlabs';

  // Fetch user's saved ElevenLabs voices
  const { data: userElevenLabsVoices, isLoading: isLoadingElevenLabsVoices } = useQuery({
    queryKey: ['/api/agents/elevenlabs/user-voices'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/agents/elevenlabs/user-voices');
      const data = await res.json();
      // Backend returns array directly; handle both formats
      const voices = Array.isArray(data) ? data : (data.voices || []);
      return voices.map((v: any) => ({
        voice_id: v.voice_id || v.elevenlabsVoiceId || v.elevenLabsVoiceId,
        name: v.name || v.displayName,
        category: v.category || undefined,
        labels: v.labels || {
          language: v.accent || v.language || '',
          gender: v.gender || '',
          accent: v.accent || '',
          age: v.age || '',
          description: v.description || '',
        },
      }));
    },
  });

  // Track loading state for ElevenLabs voices
  const isLoadingVoices = useMemo(() => {
    if (isLoadingUserData) return true;
    return isLoadingElevenLabsVoices;
  }, [isLoadingUserData, isLoadingElevenLabsVoices]);

  // User's saved ElevenLabs voices
  const userSavedVoices = useMemo(() => {
    return userElevenLabsVoices || [];
  }, [userElevenLabsVoices]);

  // Extract unique languages from user's saved voices
  // Only show languages for which the user has saved voices
  const availableLanguages = useMemo(() => {
    if (!userSavedVoices || userSavedVoices.length === 0) {
      return [];
    }
    const languageSet = new Set<string>();
    userSavedVoices.forEach((voice: any) => {
      const lang = voice.labels?.language;
      if (lang) {
        languageSet.add(lang);
      }
    });
    // Sort languages with English first, then alphabetically
    const sorted = Array.from(languageSet).sort((a, b) => {
      if (a === 'en' || a === 'en-in') return -1;
      if (b === 'en' || b === 'en-in') return 1;
      return a.localeCompare(b);
    });
    // With Nova Sonic as the voice engine, only the languages it speaks are offered
    return novaIsDefault ? filterToNovaLanguages(sorted) : sorted;
  }, [userSavedVoices, novaIsDefault]);

  // Language names mapping for display
  const languageNames: Record<string, string> = {
    en: "English",
    "en-in": "Indian English",
    de: "German",
    pl: "Polish",
    es: "Spanish",
    it: "Italian",
    fr: "French",
    pt: "Portuguese",
    ar: "Arabic",
    zh: "Chinese",
    ja: "Japanese",
    ko: "Korean",
    id: "Indonesian",
    nl: "Dutch",
    tr: "Turkish",
    fil: "Filipino",
    ms: "Malay",
    uk: "Ukrainian",
    ro: "Romanian",
    ru: "Russian",
    sv: "Swedish",
    vi: "Vietnamese",
    hu: "Hungarian",
    cs: "Czech",
    da: "Danish",
    fi: "Finnish",
    el: "Greek",
    no: "Norwegian",
    hr: "Croatian",
    bg: "Bulgarian",
    sk: "Slovak",
    th: "Thai",
  };

  const { data: voices = [] } = useQuery<Voice[]>({
    queryKey: ["/api/elevenlabs/voices"],
  });

  // Voice lookup helper - only uses account voices
  const getVoiceName = useMemo(() => {
    const voiceMap = new Map(voices.map((v) => [v.voice_id, v.name]));
    return (voiceId: string | null): string | null => {
      if (!voiceId) return null;
      return voiceMap.get(voiceId) || null;
    };
  }, [voices]);

  // The ["/api/knowledge-base"] query that used to sit here was dead: its result was
  // never read, and it was the only reason the legacy endpoint had a client. The page
  // selects knowledge bases by FOLDER, so kbFolders below is the live list.
  //
  // Fetch knowledge base folders for folder-based selection
  const { data: allKbFolders = [], isLoading: foldersLoading } = useQuery({
    queryKey: ["/api/rag-knowledge/folders"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/rag-knowledge/folders");
      return res.json();
    },
    refetchOnMount: true,
    staleTime: 0,
  });
  // Empty folders stay on the Knowledge Base page but are not offered to an agent.
  const kbFolders = foldersWithDocuments(allKbFolders, formData.knowledgeBaseFolderPath);

  // Fetch phone numbers not already assigned to agents
  const { data: unallocatedNumbers = [], isLoading: numbersLoading } = useQuery({
    queryKey: ["/api/phone-numbers", "exclude-assigned", editingAgent?.id],
    queryFn: async () => {
      const params = new URLSearchParams({ excludeAssigned: "true" });
      if (editingAgent?.id) {
        params.set("agentId", editingAgent.id);
      }
      const res = await apiRequest("GET", `/api/phone-numbers?${params.toString()}`);
      const numbers = await res.json();
      return numbers;
    },
    enabled: createDialogOpen || !!editingAgent,
    staleTime: 0, // Always refetch when dialog opens to get latest phone numbers
  });

  // Find the selected phone number from the list
  const selectedPhoneNumber = useMemo(() => {
    if (!formData.phoneNumberId || !unallocatedNumbers.length) return null;
    return unallocatedNumbers.find((n: any) => String(n.id) === String(formData.phoneNumberId));
  }, [formData.phoneNumberId, unallocatedNumbers]);

  // All orgs use ElevenLabs for voice
  const isIndianUser = normalizedBillingCountry === 'IN';

  // Fetch default LLM model from admin settings
  const { data: defaultLLMSettings } = useQuery<{ default_llm_free: string }>(
    {
      queryKey: ["/api/settings/default-llm"],
    }
  );

  // Fetch available LLM models for current user (filtered by plan tier)
  const { data: availableLLMModels = [] } = useQuery<
    Array<{
      id: string;
      modelId: string;
      name: string;
      provider: string;
      tier: "free" | "pro";
      isActive: boolean;
    }>
  >({
    queryKey: ["/api/llm-models/available"],
  });

  const { data: flows = [], isLoading: flowsLoading, error: flowsError, refetch: refetchFlows } = useQuery<
    Array<{ id: string; name: string; description: string; isActive: boolean }>
  >({
    queryKey: ["/api/flow-automation/flows"],
    staleTime: 0, // Always refetch flows to ensure we have latest data
  });

  // Handler for flow selection - auto-populates host email from appointment node
  const handleFlowSelection = async (flowId: string) => {
    setFormData(prev => ({ ...prev, flowId }));

    if (!flowId) return;

    try {
      // Fetch full flow details to extract appointment node config
      const response = await apiRequest("GET", `/api/flow-automation/flows/${flowId}`);
      const flowData = await response.json();

      if (flowData?.nodes) {
        // Find appointment node in the flow
        const appointmentNode = flowData.nodes.find(
          (n: any) => n.type === 'appointment' || n.data?.type === 'appointment' || n.data?.config?.type === 'appointment'
        );

        if (appointmentNode) {
          const config = appointmentNode.data?.config || appointmentNode.data || {};

          // Extract hostEmail from appointment node
          const hostEmail = config.hostEmail || '';

          // Auto-populate host email only (attendees are entered manually)
          if (hostEmail) {
            setFormData(prev => ({
              ...prev,
              meetingHostEmail: hostEmail,
            }));

            toast({
              title: "Host email auto-filled",
              description: `Loaded from flow's appointment settings: ${hostEmail}`,
            });
          }
        }
      }
    } catch (error) {
      console.error("Failed to fetch flow details:", error);
      // Don't show error toast - just proceed with manual entry
    }
  };

  // Filter models - Always show only OpenAI models by default (both create and edit)
  // In edit mode, the currently selected model will be added back via modelsToDisplay
  const filteredLLMModels = useMemo(() => {
    return availableLLMModels.filter(model => model.provider === 'openai');
  }, [availableLLMModels]);

  // When editing, ensure the current model is in the filtered list
  // If not, temporarily add it so it can be displayed
  const modelsToDisplay = useMemo(() => {
    if (!editingAgent || !formData.llmModel) return filteredLLMModels;
    
    // Check if current model exists in filtered list
    const modelExists = filteredLLMModels.some(m => m.modelId === formData.llmModel);
    if (modelExists) return filteredLLMModels;
    
    // Find the model in the full available list
    const currentModel = availableLLMModels.find(m => m.modelId === formData.llmModel);
    if (!currentModel) return filteredLLMModels;
    
    // Add it to the display list
    return [currentModel, ...filteredLLMModels];
  }, [filteredLLMModels, editingAgent, formData.llmModel, availableLLMModels]);

  // Voice provider is ElevenLabs for all orgs

  // Auto-set default language when dialog opens based on available voices
  useEffect(() => {
    if (!createDialogOpen || editingAgent) return; // Only on create, not edit
    if (isLoadingUserData || !userData) return; // Wait for userData to load first

    // If language is still default "en" but no English voices available, use first available
    if (availableLanguages.length > 0 && formData.language === 'en' && !availableLanguages.includes('en')) {
      setFormData(prev => ({ ...prev, language: availableLanguages[0] }));
    }
  }, [createDialogOpen, availableLanguages, editingAgent, formData.language, isLoadingUserData, userData]);

  // Auto-populate host name and email from Appointments page when creating new flow agents
  useEffect(() => {
    if (!createDialogOpen || editingAgent) return; // Only on create, not edit
    if (!existingHostData?.configured) return; // Only if host data is configured

    // Auto-populate host name if available and not already set
    if (existingHostData.hostName && !formData.meetingHostName) {
      setFormData(prev => ({ ...prev, meetingHostName: existingHostData.hostName || "" }));
    }

    // Auto-populate host email if available and not already set
    if (existingHostData.hostEmail && !formData.meetingHostEmail) {
      setFormData(prev => ({ ...prev, meetingHostEmail: existingHostData.hostEmail || "" }));
    }
  }, [createDialogOpen, editingAgent, existingHostData]);

  // Sync voice provider with billing country when userData loads
  // Voice provider is always ElevenLabs - no sync needed

  // Sync LLM model with available models - ensure the selected model exists in filtered models
  // Only sync when models are loaded and current model is invalid, or when creating a new agent
  // Skip this when editing an existing agent to preserve the selected model
  useEffect(() => {
    if (filteredLLMModels.length === 0) return;
    if (editingAgent) return; // Don't auto-sync when editing

    const modelExists = filteredLLMModels.some(
      (model) => model.modelId === formData.llmModel
    );
    if (!modelExists) {
      // Current model doesn't exist in filtered models, set to first available model
      setFormData((prev) => ({
        ...prev,
        llmModel: filteredLLMModels[0].modelId,
      }));
    }
  }, [filteredLLMModels, editingAgent, formData.llmModel]);

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await apiRequest("POST", "/api/agents", data);
      return res.json();
    },
    onSuccess: () => {
      // Trigger success animation
      setAnimationState("success");
      setTimeout(() => {
        setAnimationState("idle");
        queryClient.invalidateQueries({ queryKey: ["/api/agents"] });
        queryClient.invalidateQueries({ queryKey: ["/api/phone-numbers"] });
        setCreateDialogOpen(false);
        resetForm();
      }, 1500);
      toast({ title: t("agents.toast.created") });
    },
    onError: (error: any) => {
      // Trigger error animation
      setAnimationState("error");
      setTimeout(() => setAnimationState("idle"), 600);
      // A create can fail *after* the agent and its phone connection were written
      // (e.g. the ElevenLabs assignment step). Refresh both lists so the dialog
      // doesn't keep offering a number that is now taken.
      queryClient.invalidateQueries({ queryKey: ["/api/agents"] });
      queryClient.invalidateQueries({ queryKey: ["/api/phone-numbers"] });
      toast({
        title: t("agents.toast.createFailed"),
        description: error.message || t("agents.toast.tryAgain"),
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<typeof formData>;
    }) => {
      const res = await apiRequest("PATCH", `/api/agents/${id}`, data);
      return res.json();
    },
    onSuccess: (data) => {
      // Trigger success animation
      setAnimationState("success");
      setTimeout(() => {
        setAnimationState("idle");
        queryClient.invalidateQueries({ queryKey: ["/api/agents"] });
        queryClient.invalidateQueries({ queryKey: ["/api/phone-numbers"] });
        setEditingAgent(null);
        resetForm();
      }, 1500);

      if (data.warning) {
        toast({
          title: t("agents.toast.updated"),
          description: data.warning,
        });
      } else {
        toast({ title: t("agents.toast.updated") });
      }
    },
    onError: (error: any) => {
      // Trigger error animation
      setAnimationState("error");
      setTimeout(() => setAnimationState("idle"), 600);
      toast({
        title: t("agents.toast.updateFailed"),
        description: error.message || t("agents.toast.tryAgain"),
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/agents/${id}`);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/agents"] });
      queryClient.invalidateQueries({ queryKey: ["/api/phone-numbers"] });
      setDeletingAgent(null);

      // Show warning if ElevenLabs deletion failed
      if (data?.warning) {
        toast({
          title: t("agents.toast.deleted"),
          description: data.warning,
          variant: "default",
          duration: 8000, // Show longer for warnings
        });
      } else {
        toast({ title: t("agents.toast.deleted") });
      }
    },
    onError: (error: any) => {
      toast({
        title: t("agents.toast.deleteFailed"),
        description: error.message || t("agents.toast.tryAgain"),
        variant: "destructive",
      });
    },
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      // Delete agents sequentially
      const results = await Promise.all(
        ids.map(id => apiRequest("DELETE", `/api/agents/${id}`))
      );
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/agents"] });
      queryClient.invalidateQueries({ queryKey: ["/api/phone-numbers"] });
      setSelectedAgents([]);
      toast({ 
        title: "Agents deleted", 
        description: `Successfully deleted ${selectedAgents.length} agent${selectedAgents.length > 1 ? 's' : ''}` 
      });
    },
    onError: (error: any) => {
      toast({
        title: "Bulk delete failed",
        description: error.message || "Failed to delete agents",
        variant: "destructive",
      });
    },
  });

  const uploadKnowledgeMutation = useMutation({
    mutationFn: async () => {
      // Consolidated onto /api/rag-knowledge: the legacy /api/knowledge-base POST wrote
      // a row with no chunks and no folder, so the document was invisible to folder
      // selection and unreachable by the ask_knowledge tool — it looked saved and never
      // reached a call.
      const res = await apiRequest("POST", "/api/rag-knowledge/text", {
        name: knowledgeData.title,
        text: knowledgeData.content,
        folderPath: knowledgeData.folderPath,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/rag-knowledge"] });
      queryClient.invalidateQueries({ queryKey: ["/api/rag-knowledge/folders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/agents"] });
      setKnowledgeUploadOpen(false);
      setKnowledgeData({ title: "", type: "document", content: "", folderPath: "" });
      toast({ title: t("agents.toast.knowledgeUploaded") });
    },
    onError: (error: any) => {
      toast({
        title: t("agents.toast.knowledgeUploadFailed"),
        description: error.message || t("agents.toast.tryAgain"),
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      type: "incoming" as "incoming" | "flow",
      name: "",
      phoneNumberId: "",
      voiceProvider: "elevenlabs",
      voiceTone: "professional",
      personality: "helpful",
      systemPrompt: "",
      elevenLabsVoiceId: "",
      language: "en",
      llmModel: "gpt-4o-mini",
      firstMessage: "Hello! How can I help you today?",
      temperature: 0.5,
      knowledgeBaseIds: [],
      knowledgeBaseFolderPath: "",
      transferNumber: "",
      transferKeywords: [],
      transferEnabled: false,
      transferPhoneNumber: "",
      detectLanguageEnabled: false,
      endConversationEnabled: false,
      flowId: "",
      maxDurationSeconds: 600,
      voiceStability: 0.4,
      voiceSimilarityBoost: 0.85,
      voiceSpeed: 1.0,
      telephonyProvider: "twilio",
      salesAgentEmails: [""],
      meetingBookingEnabled: false,
    });
  };

  const handleGenerateFirstMessage = async () => {
    setIsGeneratingFirstMessage(true);
    try {
      const res = await apiRequest("POST", "/api/agents/generate-first-message", {
        agentName: formData.name,
        agentType: formData.type === 'incoming' ? 'inbound' : formData.type,
        language: formData.language || 'en',
        systemPrompt: formData.systemPrompt,
      });
      setFormData((prev) => ({ ...prev, firstMessage: res.firstMessage }));
      toast({ title: "First message generated!" });
    } catch (error: any) {
      toast({
        title: "Generation failed",
        description: error.message || "Failed to generate first message",
        variant: "destructive"
      });
    } finally {
      setIsGeneratingFirstMessage(false);
    }
  };

  const handleCreate = () => {
    const dataToSend = buildAgentCreatePayload({
      formData,
      activeVoiceProvider: "elevenlabs",
      selectedPhoneNumber: unallocatedNumbers.find(
        (n: any) => n.id === formData.phoneNumberId
      ),
      isEdit: false,
    });

    if (!formData.name) {
      toast({
        title: t("agents.toast.missingFields"),
        description: t("agents.toast.pleaseEnterName"),
        variant: "destructive",
      });
      return;
    }

    // Required, because everything the agent schedules is resolved against it and
    // the fallback is UTC — which is silently wrong for every caller outside it.
    if (!formData.timezone) {
      toast({
        title: "Timezone required",
        description: "Choose the timezone your callers are in, so the agent reads times back correctly.",
        variant: "destructive",
      });
      return;
    }

    // Incoming Agent validation
    if (formData.type === "incoming") {
      if (!formData.phoneNumberId) {
        toast({
          title: t("agents.toast.missingFields"),
          description: "Please select a phone number for this agent",
          variant: "destructive",
        });
        return;
      }
      // Voice validation: check if ElevenLabs voice is selected
      const hasVoiceSelected = !!formData.elevenLabsVoiceId;
      if (!hasVoiceSelected) {
        toast({
          title: t("agents.toast.missingFields"),
          description: t("agents.toast.pleaseSelectVoice"),
          variant: "destructive",
        });
        return;
      }
      if (!formData.systemPrompt) {
        toast({
          title: t("agents.toast.missingFields"),
          description: t("agents.toast.systemPromptRequired"),
          variant: "destructive",
        });
        return;
      }
      // Validate call transfer configuration
      if (formData.transferEnabled && !formData.transferPhoneNumber?.trim()) {
        toast({
          title: t("agents.toast.missingTransferPhone"),
          description: t("agents.toast.transferPhoneRequired"),
          variant: "destructive",
        });
        return;
      }
    }

    // Outbound Agent validation - requires phone number, flow selection, and first message
    // System prompt is NOT required for Outbound Agents as the flow defines the conversation logic
    if (formData.type === "flow") {
      // Phone number is mandatory for outbound agents
      if (!formData.phoneNumberId) {
        toast({
          title: t("agents.toast.missingFields"),
          description: "Phone number is required for outbound agents. Select a phone number to make calls from.",
          variant: "destructive",
        });
        return;
      }
      if (!formData.flowId) {
        toast({
          title: t("agents.toast.missingFields"),
          description: t("agents.toast.flowRequired"),
          variant: "destructive",
        });
        return;
      }
    }

    // Outbound Agent voice validation - check if ElevenLabs voice is selected
    if (formData.type === "flow") {
      const hasVoice = !!(formData.elevenLabsVoiceId && formData.elevenLabsVoiceId?.trim() !== "");

      if (!hasVoice) {
        // Check if voices are available for selected language
        const languageVoices = userSavedVoices?.filter((voice: any) => {
          const voiceLang = voice.labels?.language || '';
          const langCode = voiceLang.split('-')[0].toLowerCase();
          return langCode === formData.language.toLowerCase();
        });

        if (languageVoices && languageVoices.length === 0) {
          toast({
            title: t("agents.toast.missingFields"),
            description: `No voices available for ${languageNames[formData.language] || formData.language}. Please add voices from the Voices page or select a different language.`,
            variant: "destructive",
          });
        } else {
          toast({
            title: t("agents.toast.missingFields"),
            description: "Please select a voice for the outbound agent",
            variant: "destructive",
          });
        }
        return;
      }

      // Validate mandatory Host & Transfer Details for flow agents
      const missingFields: string[] = [];

      if (!formData.knowledgeBaseFolderPath?.trim()) {
        missingFields.push("Knowledge Base Folder");
      }
      if (!formData.meetingHostName?.trim()) {
        missingFields.push("Meeting Host Name (BDM Name)");
      }
      if (!formData.meetingHostEmail?.trim()) {
        missingFields.push("Meeting Host Email");
      }
      if (!formData.transferPhoneNumber?.trim()) {
        missingFields.push("Human Transfer Number");
      }
      // Meeting attendees are now optional - only host email is required

      if (missingFields.length > 0) {
        toast({
          title: "Missing Required Fields",
          description: `For flow agents, please provide: ${missingFields.join(", ")}`,
          variant: "destructive",
        });
        return;
      }
    }

    // Meeting attendees are now optional - host email is sufficient

    // Pass the dataToSend object with the correct voice provider
    createMutation.mutate(dataToSend);
  };

  const handleEdit = (agent: Agent) => {
    // Nova Sonic agents have no ElevenLabs agent behind them, so they use their own dialog.
    if (agent.voiceProvider === "nova-sonic") {
      setNovaEditingAgent(agent);
      setNovaDialogOpen(true);
      return;
    }
    setEditingAgent(agent);

    const telephonyProvider = (agent.telephonyProvider as string) || "twilio";

    setFormData({
      type: agent.type || "incoming",
      name: agent.name,
      phoneNumberId: (agent as any).connectedPhoneNumber?.id || "",
      voiceProvider: (agent.voiceProvider as any) || "elevenlabs",
      voiceTone: agent.voiceTone || "professional",
      personality: agent.personality || "helpful",
      systemPrompt: agent.systemPrompt || "",
      elevenLabsVoiceId: agent.elevenLabsVoiceId || "",
      language: agent.language || "en",
      llmModel: agent.llmModel || "gpt-4o-mini",
      firstMessage: agent.firstMessage || "Hello! How can I help you today?",
      temperature: agent.temperature ?? 0.5,
      knowledgeBaseIds: agent.knowledgeBaseIds || [],
      knowledgeBaseFolderPath: agent.knowledgeBaseFolderPath || "",
      transferNumber: agent.config?.transferRules?.number || "",
      transferKeywords: agent.config?.transferRules?.keywords || [],
      transferEnabled: agent.transferEnabled ?? false,
      transferPhoneNumber: agent.transferPhoneNumber || "",
      detectLanguageEnabled: agent.detectLanguageEnabled ?? false,
      endConversationEnabled: agent.endConversationEnabled ?? false,
      flowId: agent.flowId || "",
      maxDurationSeconds: agent.maxDurationSeconds ?? 600,
      voiceStability: agent.voiceStability ?? 0.4,
      voiceSimilarityBoost: agent.voiceSimilarityBoost ?? 0.85,
      voiceSpeed: agent.voiceSpeed ?? 1.0,
      telephonyProvider,
      salesAgentEmails: agent.salesAgentEmails?.length ? agent.salesAgentEmails : [""],
      meetingBookingEnabled: (agent as any).meetingBookingEnabled ?? false,
      meetingHostName: (agent as any).meetingHostName || "",
      meetingHostEmail: (agent as any).meetingHostEmail || "",
      meetingBookingUrl: (agent as any).meetingBookingUrl || "",
    });
  };

  const handleUpdate = () => {
    if (!editingAgent) return;

    // Meeting attendees are now optional - host email is sufficient

    // Validate call transfer configuration for incoming agents
    if (
      formData.type === "incoming" &&
      formData.transferEnabled &&
      !formData.transferPhoneNumber?.trim()
    ) {
      toast({
        title: t("agents.toast.missingTransferPhone"),
        description: t("agents.toast.transferPhoneRequired"),
        variant: "destructive",
      });
      return;
    }

    updateMutation.mutate({ id: editingAgent.id, data: formData });
  };

  const filteredAgents = agents.filter((agent) => {
    // Filter by type
    if (typeFilter !== "all" && agent.type !== typeFilter) {
      return false;
    }
    // Filter by language
    if (languageFilter !== "all" && agent.language !== languageFilter) {
      return false;
    }
    // Filter by search query
    return agent.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Pagination for agents grid
  const {
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    paginatedItems,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(filteredAgents, 9);

  const incomingCount = agents.filter((a) => a.type === "incoming").length;
  const flowCount = agents.filter((a) => a.type === "flow").length;

  return (
    <div className="space-y-6">
      {/* Page Header with Light Gradient */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 via-indigo-50 to-violet-50 dark:from-blue-950/40 dark:via-indigo-950/40 dark:to-violet-950/40 border border-blue-100 dark:border-blue-900/50 p-6 md:p-8">
        <div className="absolute inset-0 bg-grid-slate-200/50 dark:bg-grid-slate-700/20 [mask-image:linear-gradient(0deg,transparent,rgba(255,255,255,0.5))]" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Bot className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                {t("agents.title")}
              </h1>
              <p className="text-muted-foreground mt-0.5">
                {t("agents.description")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* The main button creates an agent on the platform's voice engine (set by the super admin);
                the other button creates one on the other engine. */}
            <Button
              onClick={() => (novaIsDefault ? openNovaDialog() : setCreateDialogOpen(true))}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg"
              data-testid="button-create-agent"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("agents.createNew")}
            </Button>
            {/* With ElevenLabs selected, the VoicePro Outbound Agent is offered next to it. With Nova Sonic selected,
                it is the main button above and there is no ElevenLabs button. */}
            {!novaIsDefault && (
              <Button variant="outline" onClick={openNovaDialog} data-testid="button-create-nova-sonic-agent">
                <Plus className="h-4 w-4 mr-2" />
                VoicePro Outbound Agent
              </Button>
            )}
          </div>
        </div>

        {/* Stats Row */}
        <div className="relative mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-blue-100/50 dark:border-blue-800/30">
            <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
              {agents.length}
            </div>
            <div className="text-blue-600/70 dark:text-blue-400/70 text-sm">
              {t("agents.stats.totalAgents")}
            </div>
          </div>
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-emerald-100/50 dark:border-emerald-800/30">
            <div className="flex items-center gap-2">
              <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                {incomingCount}
              </div>
              <Sparkles className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
            </div>
            <div className="text-emerald-600/70 dark:text-emerald-400/70 text-sm">
              {t("agents.type.incoming")}
            </div>
          </div>
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-violet-100/50 dark:border-violet-800/30">
            <div className="flex items-center gap-2">
              <div className="text-2xl font-bold text-violet-700 dark:text-violet-300">
                {flowCount}
              </div>
              <GitBranch className="h-4 w-4 text-violet-500 dark:text-violet-400" />
            </div>
            <div className="text-violet-600/70 dark:text-violet-400/70 text-sm">
              {t("agents.type.flow")}
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search Row */}
      <div className="space-y-4">
        {/* Primary Filters Row */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          {/* Type Filter Tabs */}
          <div className="flex items-center gap-2 p-1 bg-muted/50 rounded-lg">
          <Button
            variant={typeFilter === "all" ? "default" : "ghost"}
            size="sm"
            onClick={() => setTypeFilter("all")}
            className={typeFilter === "all" ? "" : "text-muted-foreground"}
            data-testid="button-filter-all"
          >
            {t("common.all")} ({agents.length})
          </Button>
          <Button
            variant={typeFilter === "incoming" ? "default" : "ghost"}
            size="sm"
            onClick={() => setTypeFilter("incoming")}
            className={
              typeFilter === "incoming"
                ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                : "text-muted-foreground hover:text-emerald-600"
            }
            data-testid="button-filter-incoming"
          >
            <Sparkles className="h-4 w-4 mr-1.5" />
            {t("agents.type.incoming")} ({incomingCount})
          </Button>
          <Button
            variant={typeFilter === "flow" ? "default" : "ghost"}
            size="sm"
            onClick={() => setTypeFilter("flow")}
            className={
              typeFilter === "flow"
                ? "bg-violet-600 hover:bg-violet-700 text-white"
                : "text-muted-foreground hover:text-violet-600"
            }
            data-testid="button-filter-flow"
          >
            <GitBranch className="h-4 w-4 mr-1.5" />
            {t("agents.type.flow")} ({flowCount})
          </Button>
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("agents.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="input-search-agents"
            />
          </div>
        </div>

        {/* Advanced Filters Row */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Filter by:</span>
          
          {/* Language Filter */}
          <Select value={languageFilter} onValueChange={setLanguageFilter}>
            <SelectTrigger className="w-[150px]" data-testid="select-filter-language">
              <SelectValue placeholder="Language" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Languages</SelectItem>
              {/* With Nova Sonic as the voice engine, only the languages it speaks are listed (plus any language
                  an existing agent already uses, so that agent can still be found). */}
              {AGENT_LANGUAGE_FILTERS.filter(
                (l) => !novaIsDefault || isNovaSonicLanguage(l.value) || agents.some((a) => a.language === l.value),
              ).map((l) => (
                <SelectItem key={l.value} value={l.value}>
                  {l.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Clear Filters Button */}
          {languageFilter !== "all" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setLanguageFilter("all");
              }}
              className="text-muted-foreground"
              data-testid="button-clear-filters"
            >
              Clear filters
            </Button>
          )}

          {/* Results Count */}
          <span className="text-sm text-muted-foreground ml-auto">
            {filteredAgents.length} {filteredAgents.length === 1 ? "agent" : "agents"}
          </span>
        </div>
      </div>

      {agentsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-6 animate-pulse">
              <div className="h-6 bg-muted rounded w-3/4 mb-4" />
              <div className="h-4 bg-muted rounded w-full mb-2" />
              <div className="h-4 bg-muted rounded w-2/3" />
            </Card>
          ))}
        </div>
      ) : filteredAgents.length === 0 ? (
        <div className="relative overflow-hidden rounded-3xl border border-violet-200/50 dark:border-violet-800/30 bg-gradient-to-br from-slate-50 via-white to-violet-50/50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950/30 p-16 text-center shadow-xl shadow-violet-500/5">
          {/* Animated gradient background */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-violet-100/40 via-transparent to-blue-100/30 dark:from-violet-900/20 dark:to-blue-900/10 animate-pulse" style={{ animationDuration: '4s' }} />
          
          {/* Decorative floating circles */}
          <div className="absolute top-10 left-10 h-32 w-32 rounded-full bg-gradient-to-br from-violet-400/10 to-indigo-400/10 blur-2xl animate-bounce" style={{ animationDuration: '6s' }} />
          <div className="absolute bottom-10 right-10 h-40 w-40 rounded-full bg-gradient-to-br from-blue-400/10 to-cyan-400/10 blur-2xl animate-bounce" style={{ animationDuration: '8s', animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/4 h-24 w-24 rounded-full bg-gradient-to-br from-emerald-400/10 to-teal-400/10 blur-xl animate-pulse" style={{ animationDuration: '5s' }} />
          
          {/* Grid pattern overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#8882_1px,transparent_1px),linear-gradient(to_bottom,#8882_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_40%,transparent_100%)]" />
          
          <div className="relative">
            {/* Animated icon container */}
            <div className="relative inline-block mb-8">
              {/* Outer glowing ring */}
              <div className="absolute inset-0 h-24 w-24 rounded-3xl bg-gradient-to-br from-violet-500/30 to-indigo-500/30 blur-xl animate-pulse" style={{ animationDuration: '2s' }} />
              
              {/* Icon background with gradient border */}
              <div className="relative h-24 w-24 mx-auto rounded-3xl bg-gradient-to-br from-violet-500 via-indigo-500 to-purple-600 p-[2px] shadow-2xl shadow-violet-500/30 group-hover:scale-110 transition-transform duration-500">
                <div className="h-full w-full rounded-[22px] bg-gradient-to-br from-violet-50 to-white dark:from-violet-950 dark:to-slate-900 flex items-center justify-center">
                  <Bot className="h-12 w-12 text-violet-600 dark:text-violet-400 animate-pulse" style={{ animationDuration: '3s' }} />
                </div>
              </div>
              
              {/* Sparkle decorations */}
              <div className="absolute -top-2 -right-2 h-5 w-5 animate-ping" style={{ animationDuration: '2s' }}>
                <Sparkles className="h-5 w-5 text-amber-500" />
              </div>
              <div className="absolute -bottom-1 -left-1 h-4 w-4 animate-ping" style={{ animationDuration: '3s', animationDelay: '0.5s' }}>
                <Sparkles className="h-4 w-4 text-violet-500" />
              </div>
            </div>
            
            {/* Title with gradient text */}
            <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-violet-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
              {searchQuery ? t("agents.noAgentsFound") : "Create your first agent"}
            </h3>
            
            {/* Description */}
            <p className="text-muted-foreground mb-8 max-w-lg mx-auto text-lg leading-relaxed">
              {searchQuery
                ? t("agents.noMatchingSearch")
                : "Build AI-powered voice agents to handle incoming calls or run automated campaigns with natural conversations."}
            </p>
            
            {/* Feature highlights */}
            {!searchQuery && (
              <div className="flex flex-wrap justify-center gap-4 mb-10">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200/50 dark:border-emerald-800/30">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">24/7 availability</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 dark:bg-blue-950/30 border border-blue-200/50 dark:border-blue-800/30">
                  <CheckCircle2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Natural conversations</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-violet-50 dark:bg-violet-950/30 border border-violet-200/50 dark:border-violet-800/30">
                  <CheckCircle2 className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                  <span className="text-sm font-medium text-violet-700 dark:text-violet-300">AI-powered responses</span>
                </div>
              </div>
            )}
            
            {/* CTA Button with animation */}
            {!searchQuery && (
              <Button
                onClick={() => (novaIsDefault ? openNovaDialog() : setCreateDialogOpen(true))}
                disabled={createDialogOpen || novaDialogOpen}
                size="lg"
                className="relative overflow-hidden bg-gradient-to-r from-violet-600 via-indigo-600 to-purple-600 hover:from-violet-500 hover:via-indigo-500 hover:to-purple-500 text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-lg shadow-violet-500/30 hover:shadow-violet-500/50 hover:scale-105 transition-all duration-300 group"
              >
                {/* Shine effect */}
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <Plus className="h-5 w-5 mr-2 group-hover:rotate-90 transition-transform duration-300" />
                Create Your First Agent
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedItems.map((agent) => {
              const voiceName = getVoiceName(agent.elevenLabsVoiceId);
              const languageNames: Record<string, string> = {
                en: "English",
                hi: "Hindi",
                es: "Spanish",
                fr: "French",
                de: "German",
                it: "Italian",
                pt: "Portuguese",
                ja: "Japanese",
                ko: "Korean",
                zh: "Chinese",
              };
              const languageName = agent.language
                ? languageNames[agent.language] || agent.language
                : "English";

              const isIncoming = agent.type === "incoming";
              const cardGradient = isIncoming
                ? "from-emerald-500/5 via-transparent to-teal-500/5 dark:from-emerald-500/10 dark:to-teal-500/10"
                : "from-violet-500/5 via-transparent to-indigo-500/5 dark:from-violet-500/10 dark:to-indigo-500/10";
              const iconBg = isIncoming
                ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                : "bg-violet-500/10 text-violet-600 dark:text-violet-400";
              const borderColor = isIncoming
                ? "border-emerald-500/20 hover:border-emerald-500/40"
                : "border-violet-500/20 hover:border-violet-500/40";

              return (
                <Card
                  key={agent.id}
                  className={`relative overflow-hidden border ${borderColor} transition-all duration-200 hover-elevate group`}
                  data-testid={`card-agent-${agent.id}`}
                >
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${cardGradient}`}
                  />
                  <div className="relative p-5">
                    <div className="flex flex-col gap-3 mb-3">
                      <div className="flex items-start gap-3">
                        <div
                          className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}
                        >
                          {isIncoming ? (
                            <Sparkles className="h-5 w-5" />
                          ) : (
                            <GitBranch className="h-5 w-5" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3
                            className="font-semibold break-words line-clamp-2 leading-tight"
                            data-testid="text-agent-name"
                          >
                            {agent.name}
                          </h3>
                          <Badge
                            variant="outline"
                            className={`mt-1 text-xs ${
                              isIncoming
                                ? "border-emerald-500/50 text-emerald-700 dark:text-emerald-300 bg-emerald-500/10"
                                : "border-violet-500/50 text-violet-700 dark:text-violet-300 bg-violet-500/10"
                            }`}
                            data-testid={`badge-agent-type-${agent.type}`}
                          >
                            {isIncoming
                              ? t("agents.type.incoming")
                              : t("agents.type.flow")}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity -mt-2">
                        <AgentVersionHistory
                          agentId={agent.id}
                          agentName={agent.name}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEdit(agent)}
                          data-testid="button-edit-agent"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setDeletingAgent(agent)}
                          data-testid="button-delete-agent"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      {/* Outbound agents get a number too - via agents.phoneNumberId
                          rather than an incoming connection - and the list query
                          already resolves both, so there is no reason to hide it. */}
                      {agent.connectedPhoneNumber && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Phone className="h-3.5 w-3.5" />
                          <span data-testid="text-agent-phone" className="font-medium">
                            {agent.connectedPhoneNumber.phoneNumber}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <span className="capitalize">{agent.voiceTone}</span>
                        <span className="text-muted-foreground/50">•</span>
                        <span className="capitalize">{agent.personality}</span>
                      </div>
                      {agent.flow?.name && (
                        <div className="flex items-center gap-2 text-muted-foreground min-w-0">
                          <GitBranch className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate" data-testid="text-agent-flow-name">
                            {agent.flow.name}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-muted-foreground text-xs flex-wrap">
                        <span className="flex items-center gap-1.5">
                          <Languages className="h-3.5 w-3.5" />
                          {languageName}
                        </span>
                        {voiceName && (
                          <>
                            <span className="text-muted-foreground/50">•</span>
                            <span className="flex items-center gap-1.5 min-w-0">
                              <Mic className="h-3.5 w-3.5 flex-shrink-0" />
                              <span className="truncate" data-testid="text-agent-voice">{voiceName}</span>
                            </span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground text-xs">
                        <span>Created {formatDate(agent.createdAt, 'MMM d, yyyy')}</span>
                      </div>
                      {agent.config?.model &&
                        MODEL_COSTS[agent.config.model] && (
                          <div
                            className={`flex items-center gap-2 text-xs font-medium ${isIncoming ? "text-emerald-600 dark:text-emerald-400" : "text-violet-600 dark:text-violet-400"}`}
                          >
                            <span>
                              ≈ $
                              {(
                                VOICE_COST + MODEL_COSTS[agent.config.model].llm
                              ).toFixed(3)}
                              /min
                            </span>
                            <span className="opacity-50">•</span>
                            <span>{MODEL_COSTS[agent.config.model].speed}</span>
                          </div>
                        )}
                    </div>

                    {/* Always visible: the hover toolbar above does not exist on touch screens. */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3 w-full"
                      onClick={() => setTestingAgent(agent)}
                      data-testid="button-test-agent"
                    >
                      <PhoneCall className="h-4 w-4 mr-2" />
                      Test the agent
                    </Button>

                    {/* A flow agent's behaviour lives in its flow, not its system
                        prompt (which the flow compiler overrides anyway), so the
                        flow's description is the honest answer to "what does this
                        agent do?". Incoming agents still fall back to the prompt. */}
                    {(() => {
                      const summary = agent.flow?.description || agent.systemPrompt;
                      if (!summary) return null;
                      return (
                        <p
                          className="mt-3 text-xs text-muted-foreground line-clamp-3 border-t border-border/50 pt-3"
                          title={summary}
                          data-testid="text-agent-description"
                        >
                          {summary}
                        </p>
                      );
                    })()}
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Pagination */}
          <DataPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemsPerPageOptions={[9, 18, 27, 45]}
            data-testid="agents-pagination"
          />
        </div>
      )}

      <Dialog
        open={createDialogOpen || !!editingAgent}
        onOpenChange={(open) => {
          setCreateDialogOpen(open);
          if (!open) {
            setEditingAgent(null);
            resetForm();
          }
        }}
      >
        <DialogContent
          className={`max-w-2xl flex flex-col max-h-[85vh] p-0 gap-0 overflow-hidden ${animationState === "error" ? "animate-shake" : ""}`}
        >
          {/* Success Animation Overlay */}
          {animationState === "success" && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background/90 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-4 animate-in zoom-in-50 fade-in duration-300">
                <div className="h-20 w-20 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                  <CheckCircle2 className="h-10 w-10 text-white animate-in zoom-in-75 duration-300 delay-150" />
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-foreground">
                    {editingAgent
                      ? t("agents.create.agentUpdated")
                      : t("agents.create.agentCreated")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t("agents.create.readyToUse")}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Fixed Header */}
          <DialogHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
            <DialogTitle>
              {editingAgent
                ? t("agents.create.editTitle")
                : t("agents.create.title")}
            </DialogTitle>
            <DialogDescription>
              {t("agents.create.dialogDescription")}
            </DialogDescription>
          </DialogHeader>

          {/* Scrollable Form Content */}
          <div className="flex-1 overflow-y-auto px-6">
            <div className="space-y-4 py-4">
              {/* Agent Type Selector */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">
                  {t("agents.create.typeRequired")}{" "}
                  <span className="text-destructive">*</span>
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div
                    className={`relative p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                      formData.type === "incoming"
                        ? "bg-gradient-to-br from-emerald-500/20 via-emerald-400/10 to-teal-500/20 dark:from-emerald-500/30 dark:via-emerald-400/15 dark:to-teal-500/25 border-2 border-emerald-500/50 shadow-lg shadow-emerald-500/10"
                        : "bg-muted/30 hover:bg-muted/50 border-2 border-transparent hover:border-border"
                    }`}
                    onClick={() =>
                      setFormData({ ...formData, type: "incoming" })
                    }
                    data-testid="card-type-incoming"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                          formData.type === "incoming"
                            ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        <Sparkles className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4
                            className={`font-semibold ${formData.type === "incoming" ? "text-emerald-700 dark:text-emerald-300" : ""}`}
                          >
                            {t("agents.create.incomingAgent")}
                          </h4>
                          {formData.type === "incoming" && (
                            <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {t("agents.create.incomingDescription")}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`relative p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                      formData.type === "flow"
                        ? "bg-gradient-to-br from-violet-500/20 via-purple-400/10 to-indigo-500/20 dark:from-violet-500/30 dark:via-purple-400/15 dark:to-indigo-500/25 border-2 border-violet-500/50 shadow-lg shadow-violet-500/10"
                        : "bg-muted/30 hover:bg-muted/50 border-2 border-transparent hover:border-border"
                    }`}
                    onClick={() => setFormData({ ...formData, type: "flow" })}
                    data-testid="card-type-flow"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                          formData.type === "flow"
                            ? "bg-violet-500/20 text-violet-600 dark:text-violet-400"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        <GitBranch className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4
                            className={`font-semibold ${formData.type === "flow" ? "text-violet-700 dark:text-violet-300" : ""}`}
                          >
                            {t("agents.create.flowAgent")}
                          </h4>
                          {formData.type === "flow" && (
                            <div className="h-2 w-2 rounded-full bg-violet-500 animate-pulse" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {t("agents.create.flowDescription")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Agent Name - Common for both types */}
              <div className="space-y-2">
                <Label htmlFor="agent-name">
                  {t("agents.create.nameRequired")}{" "}
                  <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="agent-name"
                  placeholder={
                    formData.type === "incoming"
                      ? t("agents.create.incomingPlaceholder")
                      : t("agents.create.flowPlaceholder")
                  }
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  data-testid="input-agent-name"
                />
              </div>

              {/* Phone Number Selection - Required for all agent types */}
              <div className="space-y-2">
                <Label htmlFor="phone-number">
                  Phone Number <span className="text-destructive">*</span>
                </Label>
                {numbersLoading ? (
                  <div className="flex items-center justify-center h-10 border rounded-md bg-muted/30">
                    <span className="text-sm text-muted-foreground">Loading phone numbers...</span>
                  </div>
                ) : unallocatedNumbers.length === 0 ? (
                  <Card className="bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900">
                    <CardContent className="pt-6 text-center">
                      <Phone className="h-12 w-12 mx-auto text-amber-600 dark:text-amber-400 mb-4" />
                      <p className="font-medium text-amber-900 dark:text-amber-100 mb-2">No Phone Numbers Available</p>
                      <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
                        You need to purchase a phone number before creating an agent
                      </p>
                      <Button
                        onClick={() => {
                          setCreateDialogOpen(false);
                          window.location.href = withBase('/app/phone-numbers');
                        }}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Purchase Phone Number
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <Select
                    value={formData.phoneNumberId}
                    onValueChange={(value) => setFormData({ ...formData, phoneNumberId: value })}
                  >
                    <SelectTrigger id="phone-number" data-testid="select-phone-number">
                      <SelectValue placeholder="Select a phone number..." />
                    </SelectTrigger>
                    <SelectContent>
                      {unallocatedNumbers.map((number: any) => (
                        <SelectItem key={number.id} value={number.id}>
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-emerald-500" />
                            <span>{number.phoneNumber}</span>
                            {number.friendlyName && (
                              <span className="text-muted-foreground">({number.friendlyName})</span>
                            )}
                            <Badge variant="outline" className="ml-2">
                              {number.type}
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <p className="text-xs text-muted-foreground">
                  {formData.type === "incoming"
                    ? "This phone number will be dedicated to this agent for receiving calls"
                    : "Select a phone number to make outbound calls from. Voice provider will be set based on phone type."}
                </p>
              </div>

              {/* Incoming Agent Specific Fields - Voice & Personality */}
              {formData.type === "incoming" && (
                <>
                  {/* Voice & Personality Section Header */}
                  <div className="flex items-center gap-2 pt-2">
                    <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <Mic className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <Label className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                      {t("agents.create.voicePersonality")}
                    </Label>
                  </div>

                  {/* Language Selection - Select language FIRST to filter voices */}
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Label htmlFor="language">
                        {t("agents.create.languageRequired")} <span className="text-destructive">*</span>
                      </Label>
                      <InfoTooltip content={t("agents.create.languageTooltip")} />
                    </div>
                    {/* Language selection based on effective voice provider (phone type for Indian org) */}
                    <Select
                      value={formData.language}
                      onValueChange={(value) => {
                        setFormData({
                          ...formData,
                          language: value,
                          // Clear voice selection when language changes
                          elevenLabsVoiceId: "",
                        });
                      }}
                    >
                      <SelectTrigger id="language" data-testid="select-language">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {isLoadingVoices ? (
                          <SelectItem value="loading" disabled>
                            Loading voices...
                          </SelectItem>
                        ) : availableLanguages.length === 0 ? (
                          <SelectItem value="none" disabled>
                            No saved voices available
                          </SelectItem>
                        ) : (
                          availableLanguages.map((langCode) => (
                            <SelectItem key={langCode} value={langCode}>
                              {languageNames[langCode] || langCode.toUpperCase()}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Select language first to filter compatible voices
                    </p>
                  </div>

                  {/* Sits with Language because both describe where the caller is.
                      Callers say "three tomorrow", never "11:00 UTC", and with no
                      zone set that resolved against UTC — offering a Riyadh caller
                      slots in the middle of the night and booking callbacks hours
                      out. The edit dialog gained this field first; creating an
                      agent skipped it entirely and started every one at null. */}
                  <div className="space-y-2">
                    <Label htmlFor="timezone">
                      Timezone <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={formData.timezone}
                      onValueChange={(value) => setFormData({ ...formData, timezone: value })}
                    >
                      <SelectTrigger id="timezone" data-testid="select-timezone">
                        <SelectValue placeholder="Select a timezone" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {AGENT_TIMEZONE_OPTIONS.map((tz) => (
                          <SelectItem key={tz} value={tz}>
                            {tz === AGENT_BROWSER_TIMEZONE ? `${tz} (your timezone)` : tz}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Where your callers are. Used to read back times like "three tomorrow" for
                      callbacks and bookings — e.g. Asia/Riyadh for AST, Asia/Kolkata for IST.
                    </p>
                  </div>

                  {/* Voice Selection - Filtered by selected language from saved voices */}
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Label htmlFor="voice">
                        {t("agents.create.voiceRequired")} <span className="text-destructive">*</span>
                      </Label>
                      <InfoTooltip content={t("agents.create.voiceTooltip")} />
                    </div>
                    {isLoadingUserData ? (
                      <div className="flex items-center justify-center h-10 border rounded-md bg-muted/30">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <ElevenLabsVoicePicker
                        value={formData.elevenLabsVoiceId}
                        onChange={(voiceId: string) =>
                          setFormData({
                            ...formData,
                            elevenLabsVoiceId: voiceId,
                          })
                        }
                        language={formData.language}
                        savedVoices={userSavedVoices}
                      />
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Label htmlFor="voice-tone">
                          {t("agents.create.voiceTone")}
                        </Label>
                        <InfoTooltip
                          content={t("agents.create.voiceToneTooltip")}
                        />
                      </div>
                      <Select
                        value={formData.voiceTone}
                        onValueChange={(value) =>
                          setFormData({ ...formData, voiceTone: value })
                        }
                      >
                        <SelectTrigger
                          id="voice-tone"
                          data-testid="select-voice-tone"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="professional">
                            {t("agents.voiceTones.professional")}
                          </SelectItem>
                          <SelectItem value="friendly">
                            {t("agents.voiceTones.friendly")}
                          </SelectItem>
                          <SelectItem value="casual">
                            {t("agents.voiceTones.casual")}
                          </SelectItem>
                          <SelectItem value="formal">
                            {t("agents.voiceTones.formal")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Label htmlFor="personality">
                          {t("agents.create.personality")}
                        </Label>
                        <InfoTooltip
                          content={t("agents.create.personalityTooltip")}
                        />
                      </div>
                      <Select
                        value={formData.personality}
                        onValueChange={(value) =>
                          setFormData({ ...formData, personality: value })
                        }
                      >
                        <SelectTrigger
                          id="personality"
                          data-testid="select-personality"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="helpful">
                            {t("agents.personalities.helpful")}
                          </SelectItem>
                          <SelectItem value="enthusiastic">
                            {t("agents.personalities.enthusiastic")}
                          </SelectItem>
                          <SelectItem value="empathetic">
                            {t("agents.personalities.empathetic")}
                          </SelectItem>
                          <SelectItem value="direct">
                            {t("agents.personalities.direct")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              )}

              {/* Outbound Agent Specific Fields */}
              {formData.type === "flow" && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Label htmlFor="flow-select">
                        {t("agents.create.conversationFlowRequired")}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <InfoTooltip
                        content={t("agents.create.conversationFlowTooltip")}
                      />
                    </div>
                    <Select
                      value={formData.flowId}
                      onValueChange={handleFlowSelection}
                      disabled={flowsLoading}
                    >
                      <SelectTrigger id="flow-select" data-testid="select-flow">
                        <SelectValue
                          placeholder={flowsLoading ? "Loading flows..." : t("agents.create.selectFlow")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {flowsLoading ? (
                          <SelectItem value="loading" disabled>
                            Loading flows...
                          </SelectItem>
                        ) : flowsError ? (
                          <SelectItem value="error" disabled>
                            Error loading flows. Click refresh to retry.
                          </SelectItem>
                        ) : flows.length === 0 ? (
                          <SelectItem value="no-flows-available" disabled>
                            {t("agents.create.noFlowsAvailable")}
                          </SelectItem>
                        ) : (
                          <>
                            {/* Active flows - selectable */}
                            {flows.filter(flow => flow.isActive).map((flow) => (
                              <SelectItem key={flow.id} value={flow.id}>
                                {flow.name}
                              </SelectItem>
                            ))}
                            {/* Inactive flows - shown but disabled */}
                            {flows.filter(flow => !flow.isActive).map((flow) => (
                              <SelectItem key={flow.id} value={flow.id} disabled className="text-muted-foreground">
                                {flow.name} (Inactive)
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    {flowsError && (
                      <p className="text-xs text-destructive flex items-center gap-2">
                        Failed to load flows.
                        <button
                          type="button"
                          className="underline hover:no-underline"
                          onClick={() => refetchFlows()}
                        >
                          Retry
                        </button>
                      </p>
                    )}
                    {!flowsLoading && !flowsError && flows.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        {t("agents.create.createFlowFirst")}
                      </p>
                    )}
                    {!flowsLoading && !flowsError && flows.length > 0 && flows.filter(f => f.isActive).length === 0 && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">
                        All your flows are inactive. Please activate a flow in the Visual Flows page first.
                      </p>
                    )}
                  </div>

                  {/* Language Selection for Outbound Agents - Select language FIRST to filter voices */}
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Label htmlFor="flow-language">
                        {t("agents.create.languageRequired")} <span className="text-destructive">*</span>
                      </Label>
                      <InfoTooltip content={t("agents.create.languageTooltip")} />
                    </div>
                    <Select
                      value={formData.language}
                      onValueChange={(value) => {
                        // Reset voice when language changes
                        setFormData({
                          ...formData,
                          language: value,
                          elevenLabsVoiceId: "" // Clear voice selection when language changes
                        });
                      }}
                    >
                      <SelectTrigger id="flow-language" data-testid="select-flow-language">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {isLoadingVoices ? (
                          <SelectItem value="loading" disabled>
                            Loading voices...
                          </SelectItem>
                        ) : availableLanguages.length === 0 ? (
                          <SelectItem value="none" disabled>
                            No saved voices available
                          </SelectItem>
                        ) : (
                          availableLanguages.map((langCode) => (
                            <SelectItem key={langCode} value={langCode}>
                              {languageNames[langCode] || langCode.toUpperCase()}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Select language first to filter compatible voices
                    </p>
                  </div>

                  {/* Voice Selection for Outbound Agents - Uses saved voices */}
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Label htmlFor="flow-voice">
                        {t("agents.create.voiceRequired")} <span className="text-destructive">*</span>
                      </Label>
                      <InfoTooltip content={t("agents.create.voiceTooltip")} />
                    </div>
                    {isLoadingUserData ? (
                      <div className="flex items-center justify-center h-10 border rounded-md bg-muted/30">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <ElevenLabsVoicePicker
                        value={formData.elevenLabsVoiceId}
                        onChange={(voiceId: string) =>
                          setFormData({
                            ...formData,
                            elevenLabsVoiceId: voiceId,
                          })
                        }
                        language={formData.language}
                        savedVoices={userSavedVoices}
                      />
                    )}
                  </div>

                  {/* Max Conversation Duration */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Label>
                          {t("agents.create.maxConversationDuration")}
                        </Label>
                        <InfoTooltip
                          content={t("agents.create.maxDurationTooltip")}
                        />
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {Math.round(formData.maxDurationSeconds / 60)} min
                      </span>
                    </div>
                    <Slider
                      min={60}
                      max={1800}
                      step={60}
                      value={[formData.maxDurationSeconds]}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          maxDurationSeconds: value[0],
                        })
                      }
                      data-testid="slider-max-duration"
                    />
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>1 min</span>
                      <span>30 min</span>
                    </div>
                  </div>

                  {/* Voice Settings for Outbound Agents - Hidden from customers */}
                  <div className="space-y-3 border-t pt-4" style={{display: 'none'}}>
                    <Label className="text-base">
                      {t("agents.create.voiceFineTuning")}
                    </Label>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Label>{t("agents.create.stability")}</Label>
                          <InfoTooltip
                            content={t("agents.create.stabilityTooltip")}
                          />
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {Math.round(formData.voiceStability * 100)}%
                        </span>
                      </div>
                      <Slider
                        min={0}
                        max={1}
                        step={0.05}
                        value={[formData.voiceStability]}
                        onValueChange={(value) =>
                          setFormData({ ...formData, voiceStability: value[0] })
                        }
                        data-testid="slider-voice-stability"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Label>{t("agents.create.similarityBoost")}</Label>
                          <InfoTooltip
                            content={t("agents.create.similarityBoostTooltip")}
                          />
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {Math.round(formData.voiceSimilarityBoost * 100)}%
                        </span>
                      </div>
                      <Slider
                        min={0}
                        max={1}
                        step={0.05}
                        value={[formData.voiceSimilarityBoost]}
                        onValueChange={(value) =>
                          setFormData({
                            ...formData,
                            voiceSimilarityBoost: value[0],
                          })
                        }
                        data-testid="slider-voice-similarity"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Label>{t("agents.create.speechSpeed")}</Label>
                          <InfoTooltip
                            content={t("agents.create.speechSpeedTooltip")}
                          />
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {formData.voiceSpeed.toFixed(2)}x
                        </span>
                      </div>
                      <Slider
                        min={0.7}
                        max={1.2}
                        step={0.05}
                        value={[formData.voiceSpeed]}
                        onValueChange={(value) =>
                          setFormData({ ...formData, voiceSpeed: value[0] })
                        }
                        data-testid="slider-voice-speed"
                      />
                    </div>
                  </div>

                  {/* System Tools Section for Outbound Agents */}
                  <div className="space-y-4 border-t pt-4">
                    <div className="flex items-center">
                      <Label className="text-base">
                        {t("agents.create.systemTools")}
                      </Label>
                      <InfoTooltip
                        content={t("agents.create.systemToolsTooltip")}
                      />
                    </div>

                    {/* Language Detection Toggle */}
                    <label
                      className="flex items-center gap-3 cursor-pointer"
                      data-testid="label-flow-enable-language-detection"
                    >
                      <Checkbox
                        checked={formData.detectLanguageEnabled}
                        onCheckedChange={(checked) =>
                          setFormData({
                            ...formData,
                            detectLanguageEnabled: checked as boolean,
                          })
                        }
                        data-testid="checkbox-flow-enable-language-detection"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-medium">
                            {t("agents.create.enableLanguageDetection")}
                          </span>
                          <InfoTooltip
                            content={t(
                              "agents.create.languageDetectionTooltip"
                            )}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {t("agents.create.languageDetectionDescription")}
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Outbound Agent Configuration Section */}
                  <div className="space-y-4 border-t pt-4">
                    <Label className="text-base">
                      {t("agents.create.agentConfiguration")}
                    </Label>

                    {/* LLM Model for Outbound Agents - Hidden */}
                    <div className="space-y-2" style={{ display: "none" }}>
                      <div className="flex items-center">
                        <Label htmlFor="flow-model">
                          {t("agents.create.llmModelRequired")}{" "}
                          <span className="text-destructive">*</span>
                        </Label>
                        <InfoTooltip
                          content={t("agents.create.llmModelTooltip")}
                        />
                      </div>
                    <Select
                      value={formData.llmModel}
                      onValueChange={(value) =>
                        setFormData({ ...formData, llmModel: value })
                      }
                      disabled
                    >
                      <SelectTrigger
                        id="flow-model"
                        data-testid="select-flow-model"
                        disabled
                      >
                          <SelectValue
                            placeholder={
                              availableLLMModels.length === 0
                                ? t("agents.create.noModelsAvailable")
                                : t("agents.create.selectAModel")
                            }
                          />
                        </SelectTrigger>
                      <SelectContent>
                        {modelsToDisplay.length === 0 ? (
                          <SelectItem value="no-models" disabled>
                            {t("agents.create.noModelsAvailable")}
                          </SelectItem>
                        ) : (
                          modelsToDisplay.map((model) => (
                              <SelectItem key={model.id} value={model.modelId}>
                                <div className="flex items-center gap-2">
                                  <span>{model.name}</span>
                                  {model.tier === "free" ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                                      <Check className="h-3 w-3" />
                                      {t("agents.create.free")}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-violet-500/20 to-purple-500/20 text-violet-700 dark:text-violet-300 border border-violet-300/50 dark:border-violet-500/30">
                                      <Sparkles className="h-3 w-3" />
                                      {t("agents.create.pro")}
                                    </span>
                                  )}
                                </div>
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        {t("agents.create.chooseModelFlow")}
                      </p>
                    </div>

                    {/* Temperature for Outbound Agents - Hidden */}
                    <div className="space-y-2" style={{ display: "none" }}>
                      <div className="flex items-center">
                        <Label>{t("agents.create.temperature")}</Label>
                        <InfoTooltip
                          content={t("agents.create.temperatureTooltip")}
                        />
                      </div>
                      <div className="space-y-4">
                        <Slider
                          min={0}
                          max={1}
                          step={0.1}
                          value={[formData.temperature]}
                          onValueChange={(value) =>
                            setFormData({ ...formData, temperature: value[0] })
                          }
                          data-testid="slider-flow-temperature"
                        />
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">
                            {t("agents.create.current")}:{" "}
                            {formData.temperature.toFixed(1)}
                          </span>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant={
                                formData.temperature === 0.0
                                  ? "default"
                                  : "outline"
                              }
                              size="sm"
                              onClick={() =>
                                setFormData({ ...formData, temperature: 0.0 })
                              }
                              data-testid="button-flow-temp-deterministic"
                            >
                              {t("agents.create.deterministic")}
                            </Button>
                            <Button
                              type="button"
                              variant={
                                formData.temperature === 0.5
                                  ? "default"
                                  : "outline"
                              }
                              size="sm"
                              onClick={() =>
                                setFormData({ ...formData, temperature: 0.5 })
                              }
                              data-testid="button-flow-temp-creative"
                            >
                              {t("agents.create.creative")}
                            </Button>
                            <Button
                              type="button"
                              variant={
                                formData.temperature === 1.0
                                  ? "default"
                                  : "outline"
                              }
                              size="sm"
                              onClick={() =>
                                setFormData({ ...formData, temperature: 1.0 })
                              }
                              data-testid="button-flow-temp-more-creative"
                            >
                              {t("agents.create.moreCreative")}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Agent Style - Optional behavior instructions that complement the flow */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Label htmlFor="flow-agent-style">
                            {t("agents.create.agentStyle")}
                          </Label>
                          <Badge variant="outline" className="ml-2 text-xs">
                            {t("agents.create.optional")}
                          </Badge>
                          <InfoTooltip
                            content={t("agents.create.agentStyleTooltip")}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            if (!formData.flowId) {
                              toast({
                                title: "Select a flow first",
                                description: "Please select a conversation flow to generate style from",
                                variant: "destructive",
                              });
                              return;
                            }
                            setIsGeneratingStyle(true);
                            try {
                              const res = await apiRequest("POST", "/api/agents/generate-style-from-flow", {
                                flowId: formData.flowId,
                                language: formData.language,
                              });
                              const data = await res.json();
                              if (data.agentStyle) {
                                setFormData({ ...formData, systemPrompt: data.agentStyle });
                                toast({
                                  title: "Style generated!",
                                  description: "Agent style instructions generated from flow",
                                });
                              }
                            } catch (error: any) {
                              toast({
                                title: "Generation failed",
                                description: error.message || "Failed to generate style",
                                variant: "destructive",
                              });
                            } finally {
                              setIsGeneratingStyle(false);
                            }
                          }}
                          disabled={isGeneratingStyle || !formData.flowId}
                          className="h-7 text-xs gap-1.5"
                        >
                          {isGeneratingStyle ? (
                            <>
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-3 w-3" />
                              Generate from Flow
                            </>
                          )}
                        </Button>
                      </div>
                      <Textarea
                        id="flow-agent-style"
                        placeholder={t("agents.create.agentStylePlaceholder")}
                        value={formData.systemPrompt}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            systemPrompt: e.target.value,
                          })
                        }
                        rows={3}
                        data-testid="input-flow-agent-style"
                      />
                      <p className="text-xs text-muted-foreground">
                        {t("agents.create.agentStyleHint")}
                      </p>
                    </div>

                    {/* Host & Transfer Details - Required for Flow Agents */}
                    <div className="space-y-4 p-4 bg-muted/50 rounded-lg border">
                      <div className="flex items-center gap-2 text-sm font-medium text-primary">
                        <Settings2 className="h-4 w-4" />
                        Host & Transfer Details
                        <Badge variant="outline" className="text-xs text-orange-600 border-orange-600">Required</Badge>
                      </div>

                      {/* Meeting Host Name (BDM Name) */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          Meeting Host Name
                          <span className="text-red-500">*</span>
                        </Label>
                        {existingHostData?.configured && existingHostData?.hostName ? (
                          <div className="space-y-2">
                            {/* Show existing host name as a quick-select button */}
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant={formData.meetingHostName === existingHostData.hostName ? "default" : "outline"}
                                size="sm"
                                onClick={() => setFormData({ ...formData, meetingHostName: existingHostData.hostName || "" })}
                                className="flex items-center gap-2"
                              >
                                <CheckCircle2 className="h-3 w-3 text-green-500" />
                                {existingHostData.hostName}
                                <Badge variant="secondary" className="text-xs">From Appointments</Badge>
                              </Button>
                            </div>
                            {/* Always show input for custom/different name */}
                            <Input
                              placeholder="Or enter different name..."
                              value={formData.meetingHostName === existingHostData.hostName ? "" : formData.meetingHostName}
                              onChange={(e) => setFormData({ ...formData, meetingHostName: e.target.value })}
                              className={!formData.meetingHostName?.trim() ? "border-orange-300" : ""}
                              data-testid="input-meeting-host-name"
                            />
                          </div>
                        ) : (
                          <Input
                            placeholder="e.g., Manny, John Smith"
                            value={formData.meetingHostName}
                            onChange={(e) => setFormData({ ...formData, meetingHostName: e.target.value })}
                            className={!formData.meetingHostName?.trim() ? "border-orange-300" : ""}
                            data-testid="input-meeting-host-name"
                          />
                        )}
                        <p className="text-xs text-muted-foreground">
                          Name of the BDM/rep who will host demos. Maps to {"{{bdm_name}}"} placeholder in flows.
                        </p>
                      </div>

                      {/* Meeting Host Email */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          Meeting Host Email
                          <span className="text-red-500">*</span>
                        </Label>
                        {existingHostData?.configured && existingHostData?.hostEmail ? (
                          <div className="space-y-2">
                            {/* Show existing host email as a quick-select button */}
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant={formData.meetingHostEmail === existingHostData.hostEmail ? "default" : "outline"}
                                size="sm"
                                onClick={() => setFormData({ ...formData, meetingHostEmail: existingHostData.hostEmail || "" })}
                                className="flex items-center gap-2"
                              >
                                <CheckCircle2 className="h-3 w-3 text-green-500" />
                                {existingHostData.hostEmail}
                                <Badge variant="secondary" className="text-xs">From Appointments</Badge>
                              </Button>
                            </div>
                            {/* Always show input for custom/different email */}
                            <Input
                              type="email"
                              placeholder="Or enter different email..."
                              value={formData.meetingHostEmail === existingHostData.hostEmail ? "" : formData.meetingHostEmail}
                              onChange={(e) => setFormData({ ...formData, meetingHostEmail: e.target.value })}
                              className={!formData.meetingHostEmail?.trim() ? "border-orange-300" : ""}
                              data-testid="input-meeting-host-email"
                            />
                          </div>
                        ) : (
                          <Input
                            type="email"
                            placeholder="host@example.com"
                            value={formData.meetingHostEmail}
                            onChange={(e) => setFormData({ ...formData, meetingHostEmail: e.target.value })}
                            className={!formData.meetingHostEmail?.trim() ? "border-orange-300" : ""}
                            data-testid="input-meeting-host-email"
                          />
                        )}
                        <p className="text-xs text-muted-foreground">
                          Email of the meeting host. Will receive calendar invites for booked demos.
                        </p>
                      </div>

                      {/* Call Transfer Number */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          Human Transfer Number
                          <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          type="tel"
                          placeholder="+1 555-123-4567"
                          value={formData.transferPhoneNumber}
                          onChange={(e) => setFormData({ ...formData, transferPhoneNumber: e.target.value, transferEnabled: !!e.target.value.trim() })}
                          className={!formData.transferPhoneNumber?.trim() ? "border-orange-300" : ""}
                          data-testid="input-transfer-phone"
                        />
                        <p className="text-xs text-muted-foreground">
                          Phone number to transfer calls when caller requests to speak with a human. Maps to {"{{transfer_number}}"} placeholder.
                        </p>
                      </div>
                    </div>

                    {/* Meeting Attendees */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Additional Meeting Attendees
                        <span className="text-muted-foreground text-xs ml-1">(optional)</span>
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Add team members who should also receive meeting invites. Leave empty if only the host needs to attend.
                      </p>
                      {formData.salesAgentEmails.map((email, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            type="email"
                            value={email}
                            onChange={(e) => {
                              const newEmails = [...formData.salesAgentEmails];
                              newEmails[index] = e.target.value;
                              setFormData({ ...formData, salesAgentEmails: newEmails });
                            }}
                            placeholder="attendee@example.com"
                            className="flex-1"
                            data-testid={`input-meeting-attendee-${index}`}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            disabled={formData.salesAgentEmails.length <= 1}
                            onClick={() => {
                              if (formData.salesAgentEmails.length <= 1) return;
                              const newEmails = formData.salesAgentEmails.filter((_, i) => i !== index);
                              setFormData({ ...formData, salesAgentEmails: newEmails });
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setFormData({ ...formData, salesAgentEmails: [...formData.salesAgentEmails, ""] })}
                        className="w-full"
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        Add Attendee Email
                      </Button>
                    </div>

                    {/* Knowledge Base - Folder Selection */}
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Label htmlFor="flow-kb-folder">{t("agents.create.knowledgeBase")} <span className="text-red-500">*</span></Label>
                        <InfoTooltip
                          content={t("agents.create.knowledgeBaseTooltip")}
                        />
                      </div>
                      {foldersLoading ? (
                        <div className="flex items-center justify-center h-10 border rounded-md bg-muted/30">
                          <span className="text-sm text-muted-foreground">Loading folders...</span>
                        </div>
                      ) : kbFolders.length === 0 ? (
                        <div className="border rounded-md p-4 bg-muted/30">
                          <p className="text-sm text-center text-muted-foreground">
                            No folders with documents found. Add documents to a folder in your Knowledge Base first.
                          </p>
                        </div>
                      ) : (
                        <Select
                          value={formData.knowledgeBaseFolderPath}
                          onValueChange={(value) => setFormData({ ...formData, knowledgeBaseFolderPath: value })}
                        >
                          <SelectTrigger id="flow-kb-folder" data-testid="select-flow-kb-folder" className={!formData.knowledgeBaseFolderPath ? "border-orange-300" : ""}>
                            <SelectValue placeholder="Choose a folder..." />
                          </SelectTrigger>
                          <SelectContent>
                            {kbFolders.map((folder: any) => (
                              <SelectItem key={folder.path} value={folder.path}>
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-amber-500" />
                                  <span>{folder.path === '/' ? 'Root' : folder.path.replace(/^\/+/, '')}</span>
                                  <Badge variant="outline" className="ml-2">
                                    {folder.count} {folder.count === 1 ? 'doc' : 'docs'}
                                  </Badge>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Select a folder - all documents inside will be available to your agent (required)
                      </p>
                    </div>
                  </div>
                </div>
              )}


              {/* Incoming Agent LLM and Prompt Configuration */}
              {formData.type === "incoming" && (
                <>
                  {/* AI Model Section Header (hidden – global AI config only) */}
                  <div className="flex items-center gap-2 pt-2" style={{ display: "none" }}>
                    <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                      <Brain className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <Label className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                      {t("agents.create.aiModelBehavior")}
                    </Label>
                  </div>

                  <div className="space-y-2" style={{ display: "none" }}>
                    <div className="flex items-center">
                      <Label htmlFor="model">
                        {t("agents.create.llmModelRequired")}{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <InfoTooltip
                        content={t("agents.create.llmModelTooltip")}
                      />
                    </div>
                    <Select
                      value={formData.llmModel}
                      onValueChange={(value) =>
                        setFormData({ ...formData, llmModel: value })
                      }
                    >
                      <SelectTrigger id="model" data-testid="select-model">
                        <SelectValue
                          placeholder={
                            availableLLMModels.length === 0
                              ? t("agents.create.noModelsAvailable")
                              : t("agents.create.selectAModel")
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {modelsToDisplay.length === 0 ? (
                          <SelectItem value="no-models" disabled>
                            {t("agents.create.noModelsAvailable")}
                          </SelectItem>
                        ) : (
                          modelsToDisplay.map((model) => (
                            <SelectItem key={model.id} value={model.modelId}>
                              <div className="flex items-center gap-2">
                                <span>{model.name}</span>
                                {model.tier === "free" ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                                    <Check className="h-3 w-3" />
                                    {t("agents.create.free")}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-violet-500/20 to-purple-500/20 text-violet-700 dark:text-violet-300 border border-violet-300/50 dark:border-violet-500/30">
                                    <Sparkles className="h-3 w-3" />
                                    {t("agents.create.pro")}
                                  </span>
                                )}
                              </div>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t("agents.create.chooseModel")}
                    </p>
                  </div>

                  <div className="space-y-2" style={{ display: "none" }}>
                    <div className="flex items-center">
                      <Label>{t("agents.create.temperature")}</Label>
                      <InfoTooltip
                        content={t("agents.create.temperatureTooltipFull")}
                      />
                    </div>
                    <div className="space-y-4">
                      <Slider
                        min={0}
                        max={1}
                        step={0.1}
                        value={[formData.temperature]}
                        onValueChange={(value) =>
                          setFormData({ ...formData, temperature: value[0] })
                        }
                        data-testid="slider-temperature"
                      />
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          {t("agents.create.current")}:{" "}
                          {formData.temperature.toFixed(1)}
                        </span>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant={
                              formData.temperature === 0.0
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            onClick={() =>
                              setFormData({ ...formData, temperature: 0.0 })
                            }
                            data-testid="button-temp-deterministic"
                          >
                            {t("agents.create.deterministic")}
                          </Button>
                          <Button
                            type="button"
                            variant={
                              formData.temperature === 0.5
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            onClick={() =>
                              setFormData({ ...formData, temperature: 0.5 })
                            }
                            data-testid="button-temp-creative"
                          >
                            {t("agents.create.creative")}
                          </Button>
                          <Button
                            type="button"
                            variant={
                              formData.temperature === 1.0
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            onClick={() =>
                              setFormData({ ...formData, temperature: 1.0 })
                            }
                            data-testid="button-temp-more-creative"
                          >
                            {t("agents.create.moreCreative")}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Voice Fine-Tuning Section for Incoming Agents - Hidden from customers */}
                  <div className="space-y-3 border-t pt-4" style={{display: 'none'}}>
                    <Label className="text-base">
                      {t("agents.create.voiceFineTuning")}
                    </Label>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Label>{t("agents.create.stability")}</Label>
                          <InfoTooltip
                            content={t("agents.create.stabilityTooltip")}
                          />
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {Math.round(formData.voiceStability * 100)}%
                        </span>
                      </div>
                      <Slider
                        min={0}
                        max={1}
                        step={0.05}
                        value={[formData.voiceStability]}
                        onValueChange={(value) =>
                          setFormData({ ...formData, voiceStability: value[0] })
                        }
                        data-testid="slider-incoming-voice-stability"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Label>{t("agents.create.similarityBoost")}</Label>
                          <InfoTooltip
                            content={t("agents.create.similarityBoostTooltip")}
                          />
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {Math.round(formData.voiceSimilarityBoost * 100)}%
                        </span>
                      </div>
                      <Slider
                        min={0}
                        max={1}
                        step={0.05}
                        value={[formData.voiceSimilarityBoost]}
                        onValueChange={(value) =>
                          setFormData({
                            ...formData,
                            voiceSimilarityBoost: value[0],
                          })
                        }
                        data-testid="slider-incoming-voice-similarity"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Label>{t("agents.create.speechSpeed")}</Label>
                          <InfoTooltip
                            content={t("agents.create.speechSpeedTooltip")}
                          />
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {formData.voiceSpeed.toFixed(2)}x
                        </span>
                      </div>
                      <Slider
                        min={0.7}
                        max={1.2}
                        step={0.05}
                        value={[formData.voiceSpeed]}
                        onValueChange={(value) =>
                          setFormData({ ...formData, voiceSpeed: value[0] })
                        }
                        data-testid="slider-incoming-voice-speed"
                      />
                    </div>
                  </div>

                  {/* AI Prompt Generation and Templates */}
                  <PromptTemplatesLibrary
                    mode="select"
                    onSelectTemplate={(template) => {
                      // The Agent Name field is already filled in by the time a
                      // template is picked, so resolve {{agent_name}} here rather
                      // than making the user retype it into the prompt. Anything
                      // left unresolved would be read aloud verbatim on a live
                      // call, so only substitute when we actually have a name.
                      const agentName = (formData.name || "").trim();
                      const applyAgentName = (text?: string | null) =>
                        agentName && text
                          ? text.replace(/\{\{\s*agent_name\s*\}\}/g, agentName)
                          : text;

                      // Some templates (e.g. the Inbound Lead ones) instruct the
                      // agent to book appointments via a tool, but the booking
                      // tool is only attached when "Enable Meeting Booking" is on.
                      // That checkbox otherwise stays at its default of false, so
                      // the agent ends up promising bookings it has no tool to
                      // fulfil. Auto-enable it whenever the picked template's
                      // prompt actually requires a booking tool.
                      const templateRequiresBooking =
                        !!template.systemPrompt?.includes("Booking sequence:");

                      setFormData({
                        ...formData,
                        systemPrompt:
                          applyAgentName(template.systemPrompt) ||
                          template.systemPrompt,
                        firstMessage:
                          applyAgentName(template.firstMessage) ||
                          formData.firstMessage,
                        voiceTone:
                          template.suggestedVoiceTone || formData.voiceTone,
                        personality:
                          template.suggestedPersonality || formData.personality,
                        meetingBookingEnabled:
                          templateRequiresBooking || formData.meetingBookingEnabled,
                      });

                      if (templateRequiresBooking && !formData.meetingBookingEnabled) {
                        toast({
                          title: "Meeting booking enabled",
                          description:
                            "This template books appointments, so Enable Meeting Booking was turned on for you. Turn it back off below if that's not wanted.",
                        });
                      }
                    }}
                  />

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Label htmlFor="system-prompt">
                          {t("agents.create.systemPromptRequired")}{" "}
                          <span className="text-destructive">*</span>
                        </Label>
                        <InfoTooltip
                          content={t("agents.create.systemPromptTooltipFull")}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setAiGenerateOpen(true)}
                        className="gap-1.5 text-violet-600 border-violet-200 hover:bg-violet-50 hover:text-violet-700 dark:text-violet-400 dark:border-violet-800 dark:hover:bg-violet-950"
                      >
                        <Sparkles className="h-3.5 w-3.5" />
                        Generate with AI
                      </Button>
                    </div>
                    <Textarea
                      id="system-prompt"
                      placeholder={t("agents.create.systemPromptPlaceholder")}
                      value={formData.systemPrompt}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          systemPrompt: e.target.value,
                        })
                      }
                      rows={8}
                      data-testid="input-system-prompt"
                    />
                    <p className="text-xs text-muted-foreground">
                      {t("agents.create.systemPromptTip")}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Label htmlFor="first-message">
                        {t("agents.create.firstMessage")}
                      </Label>
                      <InfoTooltip
                        content={t("agents.create.firstMessageTooltipFull")}
                      />
                    </div>
                    <Textarea
                      id="first-message"
                      placeholder={t("agents.create.firstMessagePlaceholder")}
                      value={formData.firstMessage}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          firstMessage: e.target.value,
                        })
                      }
                      rows={2}
                      data-testid="input-first-message"
                    />
                    <p className="text-xs text-muted-foreground">
                      {t("agents.create.firstMessageTip")}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Label htmlFor="kb-folder">{t("agents.create.knowledgeBase")} (Optional)</Label>
                      <InfoTooltip
                        content={t("agents.create.knowledgeBaseTooltipFull")}
                      />
                    </div>
                    {foldersLoading ? (
                      <div className="flex items-center justify-center h-10 border rounded-md bg-muted/30">
                        <span className="text-sm text-muted-foreground">Loading folders...</span>
                      </div>
                    ) : kbFolders.length === 0 ? (
                      <div className="border rounded-md p-4 bg-muted/30">
                        <p className="text-sm text-center text-muted-foreground">
                          No folders with documents found. Add documents to a folder in your Knowledge Base first.
                        </p>
                      </div>
                    ) : (
                      <Select
                        value={formData.knowledgeBaseFolderPath}
                        onValueChange={(value) => setFormData({ ...formData, knowledgeBaseFolderPath: value })}
                      >
                        <SelectTrigger id="kb-folder" data-testid="select-kb-folder">
                          <SelectValue placeholder="Choose a folder (optional)..." />
                        </SelectTrigger>
                        <SelectContent>
                          {kbFolders.map((folder: any) => (
                            <SelectItem key={folder.path} value={folder.path}>
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-amber-500" />
                                <span>{folder.path === '/' ? 'Root' : folder.path.replace(/^\/+/, '')}</span>
                                <Badge variant="outline" className="ml-2">
                                  {folder.count} {folder.count === 1 ? 'doc' : 'docs'}
                                </Badge>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Select a folder - all documents inside will be available to your agent
                    </p>
                  </div>

                  {/* System Tools Section - Only for Incoming Agents */}
                  {formData.type === "incoming" && (
                    <div className="space-y-4 pt-4">
                      {/* System Tools Section Header */}
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-rose-500/10 flex items-center justify-center">
                          <Wrench className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                        </div>
                        <div className="flex items-center gap-1">
                          <Label className="text-sm font-semibold text-rose-700 dark:text-rose-300">
                            {t("agents.create.systemTools")}
                          </Label>
                          <InfoTooltip
                            content={t("agents.systemTools.description")}
                          />
                        </div>
                      </div>

                      {/* Call Transfer Toggle */}
                      <div className="space-y-3">
                        <label
                          className="flex items-center gap-3 cursor-pointer"
                          data-testid="label-enable-transfer"
                        >
                          <Checkbox
                            checked={formData.transferEnabled}
                            onCheckedChange={(checked) =>
                              setFormData({
                                ...formData,
                                transferEnabled: checked as boolean,
                              })
                            }
                            data-testid="checkbox-enable-transfer"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-1">
                              <span className="text-sm font-medium">
                                {t("agents.systemTools.enableCallTransfer")}
                              </span>
                              <InfoTooltip
                                content={t(
                                  "agents.systemTools.callTransferTooltip"
                                )}
                              />
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {t("agents.systemTools.callTransferDescription")}
                            </p>
                          </div>
                        </label>

                        {formData.transferEnabled && (
                          <div className="ml-7 space-y-2">
                            <Label
                              htmlFor="transfer-phone"
                              className="text-sm font-normal"
                            >
                              {t("agents.systemTools.transferPhoneNumber")}{" "}
                              <span className="text-destructive">*</span>
                            </Label>
                            <Input
                              id="transfer-phone"
                              placeholder="+1234567890"
                              value={formData.transferPhoneNumber}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  transferPhoneNumber: e.target.value,
                                })
                              }
                              data-testid="input-transfer-phone"
                            />
                            <p className="text-xs text-muted-foreground">
                              {t("agents.systemTools.transferPhoneHint")}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Language Detection Toggle */}
                      <label
                        className="flex items-center gap-3 cursor-pointer"
                        data-testid="label-enable-language-detection"
                      >
                        <Checkbox
                          checked={formData.detectLanguageEnabled}
                          onCheckedChange={(checked) =>
                            setFormData({
                              ...formData,
                              detectLanguageEnabled: checked as boolean,
                            })
                          }
                          data-testid="checkbox-enable-language-detection"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-medium">
                              {t("agents.create.enableLanguageDetection")}
                            </span>
                            <InfoTooltip
                              content={t(
                                "agents.systemTools.languageDetectionTooltip"
                              )}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {t(
                              "agents.systemTools.languageDetectionDescription"
                            )}
                          </p>
                        </div>
                      </label>

                      {/* End Conversation Toggle */}
                      <label
                        className="flex items-center gap-3 cursor-pointer"
                        data-testid="label-enable-end-conversation"
                      >
                        <Checkbox
                          checked={formData.endConversationEnabled}
                          onCheckedChange={(checked) =>
                            setFormData({
                              ...formData,
                              endConversationEnabled: checked as boolean,
                            })
                          }
                          data-testid="checkbox-enable-end-conversation"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-medium">
                              {t("agents.systemTools.enableEndConversation")}
                            </span>
                            <InfoTooltip
                              content={t(
                                "agents.systemTools.endConversationTooltip"
                              )}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {t("agents.systemTools.endConversationDescription")}
                          </p>
                        </div>
                      </label>
                      
                      <label
                        className="flex items-center gap-3 cursor-pointer"
                        data-testid="label-enable-meeting-booking"
                      >
                        <Checkbox
                          checked={formData.meetingBookingEnabled}
                          onCheckedChange={(checked) =>
                            setFormData({
                              ...formData,
                              meetingBookingEnabled: checked as boolean,
                            })
                          }
                          data-testid="checkbox-enable-meeting-booking"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium">
                            Enable Meeting Booking
                          </span>
                          <p className="text-xs text-muted-foreground">
                            Allow agent to book meetings
                          </p>
                        </div>
                      </label>

                      {formData.meetingBookingEnabled && (
                        <div className="ml-7 space-y-3">
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">
                              Meeting Host Email <span className="text-muted-foreground text-xs ml-1">(optional)</span>
                            </Label>
                            <Input
                              type="email"
                              value={formData.meetingHostEmail}
                              onChange={(e) =>
                                setFormData({ ...formData, meetingHostEmail: e.target.value })
                              }
                              placeholder="host@example.com"
                              data-testid="input-meeting-host-email"
                            />
                            <p className="text-xs text-muted-foreground">
                              Calendar the appointment is booked on, and who booking confirmation/notification emails are sent to. Leave empty to use the account default calendar.
                            </p>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm font-medium">
                              Additional Meeting Attendees <span className="text-muted-foreground text-xs ml-1">(optional)</span>
                            </Label>
                            <p className="text-xs text-muted-foreground">
                              Add team members who should also receive meeting invites. Leave empty if only the host needs to attend.
                            </p>

                            {formData.salesAgentEmails.map((email, index) => (
                              <div key={index} className="flex gap-2">
                                <Input
                                  type="email"
                                  value={email}
                                  onChange={(e) => {
                                    const newEmails = [...formData.salesAgentEmails];
                                    newEmails[index] = e.target.value;
                                    setFormData({ ...formData, salesAgentEmails: newEmails });
                                  }}
                                  placeholder="agent@example.com"
                                  className="flex-1"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  disabled={formData.salesAgentEmails.length <= 1}
                                  onClick={() => {
                                    if (formData.salesAgentEmails.length <= 1) return;
                                    const newEmails = formData.salesAgentEmails.filter((_, i) => i !== index);
                                    setFormData({ ...formData, salesAgentEmails: newEmails });
                                  }}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}

                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setFormData({
                                  ...formData,
                                  salesAgentEmails: [...formData.salesAgentEmails, ""],
                                })
                              }
                              className="w-full"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add Email
                            </Button>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm font-medium">
                              Booking Link
                            </Label>
                            <Input
                              type="url"
                              value={formData.meetingBookingUrl}
                              onChange={(e) =>
                                setFormData({ ...formData, meetingBookingUrl: e.target.value })
                              }
                              placeholder="https://calendly.com/your-link"
                              data-testid="input-meeting-booking-url"
                            />
                            <p className="text-xs text-muted-foreground">
                              Optional. Your Calendly (or similar) self-schedule link — sent to the prospect after a meeting is booked so they can confirm or pick their own time.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Fixed Footer */}
          <div className="flex justify-end gap-2 px-6 py-4 border-t flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => {
                setCreateDialogOpen(false);
                setEditingAgent(null);
                resetForm();
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={editingAgent ? handleUpdate : handleCreate}
              disabled={
                createMutation.isPending ||
                updateMutation.isPending ||
                (formData.meetingBookingEnabled &&
                  !formData.salesAgentEmails.some(email => email.trim() !== ""))
              }
              data-testid="button-save-agent"
            >
              {createMutation.isPending || updateMutation.isPending
                ? t("common.saving")
                : editingAgent
                  ? t("agents.create.updateAgent")
                  : t("agents.create.createAgent")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AgentTestCallDialog
        agent={testingAgent}
        onOpenChange={(open) => !open && setTestingAgent(null)}
      />

      <NovaSonicAgentDialog
        open={novaDialogOpen}
        onOpenChange={(open) => {
          setNovaDialogOpen(open);
          if (!open) setNovaEditingAgent(null);
        }}
        agent={novaEditingAgent}
      />

      <AlertDialog
        open={!!deletingAgent}
        onOpenChange={() => setDeletingAgent(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("agents.delete.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("agents.delete.description", { name: deletingAgent?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deletingAgent && deleteMutation.mutate(deletingAgent.id)
              }
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={knowledgeUploadOpen} onOpenChange={setKnowledgeUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("agents.knowledge.uploadTitle")}</DialogTitle>
            <DialogDescription>
              {t("agents.knowledge.uploadDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="knowledge-title">
                {t("agents.knowledge.titleLabel")} *
              </Label>
              <Input
                id="knowledge-title"
                placeholder={t("agents.knowledge.titlePlaceholder")}
                value={knowledgeData.title}
                onChange={(e) =>
                  setKnowledgeData({ ...knowledgeData, title: e.target.value })
                }
                data-testid="input-knowledge-title"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="knowledge-content">
                {t("agents.knowledge.contentLabel")} *
              </Label>
              <Textarea
                id="knowledge-content"
                placeholder={t("agents.knowledge.contentPlaceholder")}
                rows={10}
                value={knowledgeData.content}
                onChange={(e) =>
                  setKnowledgeData({
                    ...knowledgeData,
                    content: e.target.value,
                  })
                }
                data-testid="input-knowledge-content"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="knowledge-folder">Folder *</Label>
              <Select
                value={knowledgeData.folderPath}
                onValueChange={(value) =>
                  setKnowledgeData({ ...knowledgeData, folderPath: value })
                }
              >
                <SelectTrigger id="knowledge-folder" data-testid="select-knowledge-folder">
                  <SelectValue placeholder="Choose a folder..." />
                </SelectTrigger>
                <SelectContent>
                  {kbFolders.map((folder: any) => (
                    <SelectItem key={folder.path} value={folder.path}>
                      {folderDisplayName(folder.path)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Agents select knowledge by folder, so a document outside one can never
                reach a call.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setKnowledgeUploadOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => uploadKnowledgeMutation.mutate()}
              disabled={
                !knowledgeData.title ||
                !knowledgeData.content ||
                !knowledgeData.folderPath ||
                uploadKnowledgeMutation.isPending
              }
              data-testid="button-upload-knowledge-submit"
            >
              {uploadKnowledgeMutation.isPending
                ? t("agents.knowledge.uploading")
                : t("agents.knowledge.uploadButton")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Agent Dialog - handled by the create/edit dialog above */}

      {/* AI Prompt Generator Dialog */}
      <Dialog open={aiGenerateOpen} onOpenChange={setAiGenerateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-violet-600" />
              Generate Prompt with AI
            </DialogTitle>
            <DialogDescription>
              Describe what your agent should do in plain English and AI will generate a professional system prompt and first message.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="ai-description">What should your agent do?</Label>
              <Textarea
                id="ai-description"
                value={aiDescription}
                onChange={(e) => setAiDescription(e.target.value)}
                placeholder="e.g., Answer calls for a dental clinic, book appointments, answer questions about services and pricing, and collect patient info..."
                className="min-h-[120px] resize-none"
                data-testid="textarea-ai-description"
              />
            </div>
            <Button
              className="w-full bg-gradient-to-r from-violet-600 to-purple-600 text-white"
              disabled={!aiDescription.trim() || aiGenerating}
              onClick={async () => {
                setAiGenerating(true);
                try {
                  const res = await apiRequest("POST", "/api/agents/generate-prompt", {
                    description: aiDescription,
                    type: formData.type === "flow" ? "outbound" : "inbound",
                    companyName: formData.name || undefined,
                    language: formData.language || "en",
                  });
                  const data = await res.json();
                  if (data.systemPrompt && data.firstMessage) {
                    setFormData((prev) => ({
                      ...prev,
                      systemPrompt: data.systemPrompt,
                      firstMessage: data.firstMessage,
                    }));
                    setAiGenerateOpen(false);
                    setAiDescription("");
                    toast({
                      title: "Prompt Generated!",
                      description: "AI has created your system prompt and first message. Feel free to edit them.",
                    });
                  }
                } catch (error: any) {
                  toast({
                    title: "Generation Failed",
                    description: error.message || "Failed to generate prompt. Please try again.",
                    variant: "destructive",
                  });
                } finally {
                  setAiGenerating(false);
                }
              }}
              data-testid="button-ai-generate-submit"
            >
              {aiGenerating ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Prompt
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
